/**
 * BTCNJCC Premium Ambient Animated Cycling Wallpaper
 * 
 * Features:
 * - Topographic elevation contours (North Jersey / Ramapo ridge profiles)
 * - Kinematic crankset chainring & sprocket rotating silhouettes (0.75 RPM)
 * - Aerodynamic wind-tunnel streamlines & peloton draft motons
 * - Zero-allocation hot loop using Float32Array (70+ FPS with 0 GC pauses)
 * - Automatic suspension on tab blur (document.hidden) to conserve 100% CPU/battery
 * - Instant static blueprint fallback for prefers-reduced-motion
 */

const PARTICLE_COUNT = 40;
const PARTICLE_STRIDE = 8; // x, y, vx, vy, life, maxLife, radius, colorType (0=cyan, 1=amber)

let canvas = null;
let ctx = null;
let offscreenCanvas = null;
let offscreenCtx = null;
let width = 0;
let height = 0;
let dpr = 1;
let rafId = null;
let lastTime = 0;
let topoPhase = 0;
let gearAngle = 0;
let mouseX = -100;
let mouseY = -100;
let clientMouseX = -100;
let clientMouseY = -100;
let mouseTargetAlpha = 0;
let mouseCurrentAlpha = 0;

// Pre-allocated typed array for particle pool
const particleData = new Float32Array(PARTICLE_COUNT * PARTICLE_STRIDE);

// Pre-allocated typed arrays for Performance Telemetry Dashboard (Zero GC)
export const SCATTER_COUNT = 48;
export const SCATTER_STRIDE = 6; // currentPwr, currentHr, basePwr, baseHr, zone, phase
export const scatterPool = new Float32Array(SCATTER_COUNT * SCATTER_STRIDE);

// Telemetry state array (Float32Array length 8):
// 0: livePwr, 1: liveHr, 2: liveCad, 3: liveWkg, 4: liveSpd, 5: liveKj, 6: r2, 7: ef
export const telemetryState = new Float32Array(8);

// Modeled Milestones for Power Duration Curve (mimicking reference WKO/CP curve)
// High neuromuscular shoulder (1s-5s), steep AWC drop (20s-1m), CP plateau (20m-1h)
const PDC_PTS = [
  { t: 0.00, pwr: 1150, lbl: '1s' },
  { t: 0.12, pwr: 1105, lbl: '5s' },   // High flat neuromuscular shoulder
  { t: 0.26, pwr: 820,  lbl: '20s' },  // Rapid glycolytic transition
  { t: 0.42, pwr: 530,  lbl: '1m' },   // Anaerobic work capacity drop
  { t: 0.58, pwr: 375,  lbl: '5m' },   // Aerobic inflection knee
  { t: 0.74, pwr: 312,  lbl: '20m' },  // Critical Power / FTP plateau
  { t: 0.88, pwr: 285,  lbl: '1h' },   // Threshold base
  { t: 1.00, pwr: 215,  lbl: '5h' }    // Aerobic endurance floor
];

// Pre-allocated color palette for training zones Z1-Z5
const ZONE_COLORS = [
  'rgba(148, 163, 184, 0.85)', // Z1 Recovery (slate)
  'rgba(56, 189, 248, 0.90)',  // Z2 Endurance (cyan)
  'rgba(52, 211, 153, 0.90)',  // Z3 Tempo (emerald)
  'rgba(251, 191, 36, 0.90)',  // Z4 Threshold (amber)
  'rgba(244, 63, 94, 0.90)'    // Z5 VO2 Max (rose)
];

// Seed scatter pool and initial telemetry state
(function initScatterAndTelemetry() {
  telemetryState[0] = 285; // PWR (W)
  telemetryState[1] = 158; // HR (BPM)
  telemetryState[2] = 92;  // CAD (RPM)
  telemetryState[3] = 4.1; // W/KG
  telemetryState[4] = 24.6;// SPD (MPH)
  telemetryState[5] = 1420;// KJ
  telemetryState[6] = 0.94;// r²
  telemetryState[7] = 1.80;// EF

  for (let i = 0; i < SCATTER_COUNT; i++) {
    const t = i / (SCATTER_COUNT - 1);
    const pseudoRand1 = ((i * 17 + 13) % 47) / 47;
    const pseudoRand2 = ((i * 31 + 7) % 53) / 53;
    const basePwr = 135 + t * 240 + (pseudoRand1 - 0.5) * 28;
    const baseHr = 78 + (basePwr * 0.275) + (pseudoRand2 - 0.5) * 10;

    let zone = 0; // 0=Z1, 1=Z2, 2=Z3, 3=Z4, 4=Z5
    if (basePwr < 175) zone = 0;
    else if (basePwr < 235) zone = 1;
    else if (basePwr < 285) zone = 2;
    else if (basePwr < 340) zone = 3;
    else zone = 4;

    const offset = i * SCATTER_STRIDE;
    scatterPool[offset] = basePwr;
    scatterPool[offset + 1] = baseHr;
    scatterPool[offset + 2] = basePwr;
    scatterPool[offset + 3] = baseHr;
    scatterPool[offset + 4] = zone;
    scatterPool[offset + 5] = (i * 0.37) % (Math.PI * 2);
  }
})();

// Pre-allocated color palette strings to avoid GC in hot loop
const COLORS = {
  cyanParticle: 'rgba(56, 189, 248, ',
  amberParticle: 'rgba(251, 191, 36, ',
  cyanGear: 'rgba(56, 189, 248, 0.16)',
  amberGear: 'rgba(251, 191, 36, 0.12)',
  streamlineCyan: 'rgba(56, 189, 248, 0.18)',
  streamlineAmber: 'rgba(251, 191, 36, 0.14)'
};

/**
 * Initialize a single particle in the typed array
 */
function initParticle(index, spawnAnywhere = false) {
  const offset = index * PARTICLE_STRIDE;
  particleData[offset] = spawnAnywhere ? Math.random() * width : -20 - Math.random() * 80;
  // Particles flow through upper-to-mid aerodynamic paths
  const laneY = height * 0.2 + (Math.random() * height * 0.65);
  particleData[offset + 1] = laneY;
  // Velocity in px per ms
  particleData[offset + 2] = 0.08 + Math.random() * 0.12; // vx
  particleData[offset + 3] = (Math.random() - 0.5) * 0.02; // vy drift
  const maxLife = 3000 + Math.random() * 3000;
  particleData[offset + 4] = spawnAnywhere ? Math.random() * maxLife : 0; // current life
  particleData[offset + 5] = maxLife;
  particleData[offset + 6] = 1.2 + Math.random() * 2.2; // radius
  particleData[offset + 7] = Math.random() > 0.35 ? 0 : 1; // 0=cyan, 1=amber
}

/**
 * Pre-render static topographic contour patterns into an OffscreenCanvas
 */
function renderOffscreenTopo() {
  if (!offscreenCanvas) {
    if (typeof OffscreenCanvas !== 'undefined') {
      offscreenCanvas = new OffscreenCanvas(Math.max(width * 2, 1200), Math.max(height, 600));
    } else {
      offscreenCanvas = document.createElement('canvas');
      offscreenCanvas.width = Math.max(width * 2, 1200);
      offscreenCanvas.height = Math.max(height, 600);
    }
    offscreenCtx = offscreenCanvas.getContext('2d');
  }

  const oW = offscreenCanvas.width;
  const oH = offscreenCanvas.height;
  offscreenCtx.clearRect(0, 0, oW, oH);

  // Draw 6 topographic contour elevation ridges (Ramapo & Bear Mountain ridges)
  const ridges = [
    { baseY: oH * 0.15, amp: 45, freq: 0.003, color: 'rgba(56, 189, 248, 0.07)', dash: [6, 4] },
    { baseY: oH * 0.28, amp: 65, freq: 0.0025, color: 'rgba(56, 189, 248, 0.09)', dash: [] },
    { baseY: oH * 0.44, amp: 85, freq: 0.002, color: 'rgba(251, 191, 36, 0.06)', dash: [10, 5] },
    { baseY: oH * 0.60, amp: 70, freq: 0.0035, color: 'rgba(56, 189, 248, 0.08)', dash: [] },
    { baseY: oH * 0.76, amp: 95, freq: 0.0022, color: 'rgba(251, 191, 36, 0.07)', dash: [] },
    { baseY: oH * 0.90, amp: 55, freq: 0.004, color: 'rgba(56, 189, 248, 0.06)', dash: [4, 4] }
  ];

  ridges.forEach(ridge => {
    offscreenCtx.beginPath();
    offscreenCtx.strokeStyle = ridge.color;
    offscreenCtx.lineWidth = 1.2;
    if (ridge.dash.length > 0) {
      offscreenCtx.setLineDash(ridge.dash);
    } else {
      offscreenCtx.setLineDash([]);
    }

    let first = true;
    for (let x = 0; x <= oW; x += 15) {
      const y = ridge.baseY +
        Math.sin(x * ridge.freq) * ridge.amp +
        Math.cos(x * ridge.freq * 0.6 + 1.2) * (ridge.amp * 0.4);
      if (first) {
        offscreenCtx.moveTo(x, y);
        first = false;
      } else {
        offscreenCtx.lineTo(x, y);
      }
    }
    offscreenCtx.stroke();
  });
}

/**
 * Draw rotating crankset chainring / cassette cog silhouette
 */
function drawChainring(centerX, centerY, outerR, innerR, teethCount, angle, colorStroke) {
  ctx.save();
  ctx.translate(centerX, centerY);
  ctx.rotate(angle);

  // Outer Chainring Ring
  ctx.beginPath();
  ctx.strokeStyle = colorStroke;
  ctx.lineWidth = 1.6;
  ctx.arc(0, 0, outerR, 0, Math.PI * 2);
  ctx.stroke();

  // Teeth mesh around circumference
  ctx.beginPath();
  ctx.strokeStyle = colorStroke;
  ctx.lineWidth = 1.2;
  const toothAngleStep = (Math.PI * 2) / teethCount;
  for (let i = 0; i < teethCount; i++) {
    const a = i * toothAngleStep;
    const cosA = Math.cos(a);
    const sinA = Math.sin(a);
    const x1 = cosA * outerR;
    const y1 = sinA * outerR;
    const x2 = cosA * (outerR + 4.5);
    const y2 = sinA * (outerR + 4.5);
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
  }
  ctx.stroke();

  // Inner Bolt Circle & Spider Arms (5-arm crank spider)
  ctx.beginPath();
  ctx.arc(0, 0, innerR, 0, Math.PI * 2);
  ctx.stroke();

  const arms = 5;
  const armStep = (Math.PI * 2) / arms;
  ctx.beginPath();
  for (let j = 0; j < arms; j++) {
    const armA = j * armStep;
    ctx.moveTo(0, 0);
    ctx.lineTo(Math.cos(armA) * outerR, Math.sin(armA) * outerR);
  }
  ctx.stroke();

  // Spindle hub
  ctx.beginPath();
  ctx.arc(0, 0, innerR * 0.35, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(9, 13, 22, 0.7)';
  ctx.fill();
  ctx.stroke();

  ctx.restore();
}

/**
 * Draw flowing aerodynamic wind-tunnel streamlines
 */
function drawStreamlines(timeMs) {
  const waveShift = timeMs * 0.0004;
  ctx.save();
  ctx.lineWidth = 1.3;

  // Streamline 1: Upper aerodynamic draft curve
  ctx.beginPath();
  ctx.strokeStyle = COLORS.streamlineCyan;
  ctx.setLineDash([160, 40]);
  ctx.lineDashOffset = -timeMs * 0.04;
  ctx.moveTo(-50, height * 0.32 + Math.sin(waveShift) * 20);
  ctx.bezierCurveTo(
    width * 0.3, height * 0.22 + Math.cos(waveShift) * 25,
    width * 0.65, height * 0.42 + Math.sin(waveShift * 0.8) * 30,
    width + 50, height * 0.30 + Math.cos(waveShift) * 15
  );
  ctx.stroke();

  // Streamline 2: Mid-lower draft curve
  ctx.beginPath();
  ctx.strokeStyle = COLORS.streamlineAmber;
  ctx.setLineDash([120, 60]);
  ctx.lineDashOffset = -timeMs * 0.055;
  ctx.moveTo(-50, height * 0.58 + Math.cos(waveShift * 0.9) * 25);
  ctx.bezierCurveTo(
    width * 0.35, height * 0.68 + Math.sin(waveShift) * 30,
    width * 0.7, height * 0.50 + Math.cos(waveShift * 0.7) * 20,
    width + 50, height * 0.62 + Math.sin(waveShift) * 20
  );
  ctx.stroke();

  ctx.restore();
}

/**
 * Advance & render zero-allocation particle motons with volumetric light-scattering boost
 */
function updateAndRenderParticles(deltaMs) {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const offset = i * PARTICLE_STRIDE;
    // Advance position
    particleData[offset] += particleData[offset + 2] * deltaMs; // x += vx * dt
    particleData[offset + 1] += particleData[offset + 3] * deltaMs; // y += vy * dt
    particleData[offset + 4] += deltaMs; // life += dt

    const life = particleData[offset + 4];
    const maxLife = particleData[offset + 5];

    // Reset if out of bounds or expired
    if (particleData[offset] > width + 40 || life >= maxLife) {
      initParticle(i, false);
      continue;
    }

    // Smooth sinusoidal alpha fade in and fade out
    const lifeRatio = life / maxLife;
    let alpha = Math.sin(lifeRatio * Math.PI) * 0.42;
    let radius = particleData[offset + 6];
    const colorType = particleData[offset + 7];
    const px = particleData[offset];
    const py = particleData[offset + 1];

    // Volumetric Mie scattering boost when particles intersect diagonal god ray zone
    const rayDist = Math.abs((px / (width || 1)) + (py / (height || 1)) - 0.95);
    if (rayDist < 0.22) {
      const boost = 1 + (0.22 - rayDist) * 3.2;
      alpha = Math.min(alpha * boost, 0.92);
      radius *= 1.25;
    }

    ctx.beginPath();
    ctx.arc(px, py, radius, 0, Math.PI * 2);
    ctx.fillStyle = (colorType === 0 ? COLORS.cyanParticle : COLORS.amberParticle) + alpha + ')';
    ctx.fill();

    // Subtle leading trail spark
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(px - particleData[offset + 2] * 40, py);
    ctx.strokeStyle = (colorType === 0 ? COLORS.cyanParticle : COLORS.amberParticle) + (alpha * 0.3) + ')';
    ctx.lineWidth = radius * 0.6;
    ctx.stroke();
  }

  ctx.restore();
}

/**
 * Draw 3 volumetric crepuscular God rays sweeping from upper-right periphery
 */
export function drawCrepuscularRays(now) {
  if (!ctx) return;
  ctx.save();
  ctx.globalCompositeOperation = 'screen';

  const originX = width * 0.95;
  const originY = -40;

  // Ray 1: Wide primary ambient beam
  const ray1Shift = Math.sin(now * 0.00032) * (width * 0.08);
  const grad1 = ctx.createLinearGradient(originX, originY, width * 0.2 + ray1Shift, height);
  grad1.addColorStop(0, 'rgba(56, 189, 248, 0.22)');
  grad1.addColorStop(0.35, 'rgba(56, 189, 248, 0.08)');
  grad1.addColorStop(0.75, 'rgba(56, 189, 248, 0.02)');
  grad1.addColorStop(1, 'rgba(56, 189, 248, 0)');

  ctx.beginPath();
  ctx.moveTo(originX - 30, originY);
  ctx.lineTo(originX + 80, originY);
  ctx.lineTo(width * 0.35 + ray1Shift, height);
  ctx.lineTo(width * 0.05 + ray1Shift, height);
  ctx.closePath();
  ctx.fillStyle = grad1;
  ctx.fill();

  // Ray 2: Slender secondary ray cutting across central bicycle frame
  const ray2Shift = Math.cos(now * 0.00025 + 0.8) * (width * 0.06);
  const grad2 = ctx.createLinearGradient(originX, originY, width * 0.45 + ray2Shift, height);
  grad2.addColorStop(0, 'rgba(56, 189, 248, 0.16)');
  grad2.addColorStop(0.4, 'rgba(56, 189, 248, 0.06)');
  grad2.addColorStop(1, 'rgba(56, 189, 248, 0)');

  ctx.beginPath();
  ctx.moveTo(originX - 10, originY);
  ctx.lineTo(originX + 40, originY);
  ctx.lineTo(width * 0.55 + ray2Shift, height);
  ctx.lineTo(width * 0.40 + ray2Shift, height);
  ctx.closePath();
  ctx.fillStyle = grad2;
  ctx.fill();

  // Ray 3: Warm amber tertiary ray grazing bottom-center bearings
  const ray3Shift = Math.sin(now * 0.00018 + 1.5) * (width * 0.05);
  const grad3 = ctx.createLinearGradient(originX, originY, width * 0.7 + ray3Shift, height);
  grad3.addColorStop(0, 'rgba(251, 191, 36, 0.14)');
  grad3.addColorStop(0.5, 'rgba(251, 191, 36, 0.04)');
  grad3.addColorStop(1, 'rgba(251, 191, 36, 0)');

  ctx.beginPath();
  ctx.moveTo(originX + 20, originY);
  ctx.lineTo(originX + 90, originY);
  ctx.lineTo(width * 0.78 + ray3Shift, height);
  ctx.lineTo(width * 0.65 + ray3Shift, height);
  ctx.closePath();
  ctx.fillStyle = grad3;
  ctx.fill();

  ctx.restore();
}

/**
 * Normalized Lockstep Coordinate Transform Engine
 * Computes exact background-size: cover destination rectangle and scale
 */
export function computeCoverBounds(viewportW, viewportH, imgW = 1024, imgH = 559) {
  const imgAspect = imgW / imgH;
  const targetAspect = (viewportW || 1) / (viewportH || 1);
  let scale, renderW, renderH, offsetX, offsetY;

  if (targetAspect > imgAspect) {
    // Canvas is wider than image aspect ratio -> fit width, crop height
    scale = viewportW / imgW;
    renderW = viewportW;
    renderH = imgH * scale;
    offsetX = 0;
    offsetY = (viewportH - renderH) / 2;
  } else {
    // Canvas is taller than image aspect ratio -> fit height, crop width
    scale = viewportH / imgH;
    renderH = viewportH;
    renderW = imgW * scale;
    offsetX = (viewportW - renderW) / 2;
    offsetY = 0;
  }

  return { scale, renderW, renderH, offsetX, offsetY };
}

/**
 * Authentic Mechanical Vertices mapped from 1024x559 Blueprint Schematic
 */
export const BLUEPRINT_COORDS = {
  // Mechanical vertices of the Bare Carbon Frame (Upper-Left Quadrant) in 1024x559 blueprint space
  bb: { x: 236, y: 498 },          // Bottom Bracket shell
  rearDropout: { x: 88, y: 458 },  // Rear Wheel Dropouts
  seatJunction: { x: 186, y: 180 },// Seat Tube / Top Tube / Seatstay junction
  seatClamp: { x: 158, y: 44 },    // Saddle Mast & Rail Clamp
  headUpper: { x: 368, y: 134 },   // Head Tube Top / Cockpit
  headLower: { x: 378, y: 230 },   // Head Tube Bottom / Fork crown
  forkDropout: { x: 430, y: 458 }, // Front Fork Dropouts

  // Exploded Hub Assembly vector (Center-Top)
  explodedHubStart: { x: 440, y: 155 },
  explodedHubEnd: { x: 660, y: 55 },

  // Drivetrain Matrix Box (Top-Right)
  matrix: { x: 738, y: 22, w: 256, h: 124 }
};

/**
 * Helper to render glowing photon with radial halo
 */
function renderPhotonHalo(px, py, r, g, b, alpha = 0.85, haloR = 16) {
  const grad = ctx.createRadialGradient(px, py, 0, px, py, haloR);
  grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${alpha})`);
  grad.addColorStop(0.4, `rgba(${r}, ${g}, ${b}, ${alpha * 0.45})`);
  grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(px, py, haloR, 0, Math.PI * 2);
  ctx.fill();

  ctx.beginPath();
  ctx.arc(px, py, 2.2, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.95})`;
  ctx.fill();
}

/**
 * Draw Lockstep Laser Circuit Tracing along the authentic bicycle diamond frame
 */
export function drawFrameLaserCircuit(now, bounds) {
  if (!ctx) return;
  const b = bounds || computeCoverBounds(width, height, 1024, 559);
  const { bb, rearDropout, seatJunction, headUpper, headLower, forkDropout, seatClamp } = BLUEPRINT_COORDS;

  ctx.save();
  ctx.translate(b.offsetX, b.offsetY);
  ctx.scale(b.scale, b.scale);

  // 1. Draw subtle ambient vector schematic guide lines along carbon tubes
  ctx.save();
  ctx.strokeStyle = 'rgba(56, 189, 248, 0.22)';
  ctx.lineWidth = 1.4;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Main Diamond Frame (Down Tube, Top Tube, Seat Tube, Head Tube)
  ctx.beginPath();
  ctx.moveTo(bb.x, bb.y);
  ctx.lineTo(seatJunction.x, seatJunction.y);
  ctx.lineTo(headUpper.x, headUpper.y);
  ctx.lineTo(headLower.x, headLower.y);
  ctx.lineTo(bb.x, bb.y);
  ctx.stroke();

  // Rear Triangle (Seatstays & Chainstays)
  ctx.beginPath();
  ctx.moveTo(bb.x, bb.y);
  ctx.lineTo(rearDropout.x, rearDropout.y);
  ctx.lineTo(seatJunction.x, seatJunction.y);
  ctx.stroke();

  // Fork Blade
  ctx.beginPath();
  ctx.moveTo(headLower.x, headLower.y);
  ctx.lineTo(forkDropout.x, forkDropout.y);
  ctx.stroke();

  // Seatpost Mast
  ctx.beginPath();
  ctx.moveTo(seatJunction.x, seatJunction.y);
  ctx.lineTo(seatClamp.x, seatClamp.y);
  ctx.strokeStyle = 'rgba(56, 189, 248, 0.16)';
  ctx.stroke();

  // Traveling Dash-Wave along frame tubes (decelerated by 40%)
  ctx.beginPath();
  ctx.strokeStyle = 'rgba(56, 189, 248, 0.45)';
  ctx.lineWidth = 1.6;
  ctx.setLineDash([14, 70]);
  ctx.lineDashOffset = -now * 0.03;
  ctx.moveTo(bb.x, bb.y);
  ctx.lineTo(seatJunction.x, seatJunction.y);
  ctx.lineTo(headUpper.x, headUpper.y);
  ctx.lineTo(headLower.x, headLower.y);
  ctx.lineTo(bb.x, bb.y);
  ctx.stroke();

  ctx.restore();

  // 2. Kinetic Photonic Energy Packets traveling along frame tubes (decelerated by 40%)
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';

  // Photon 1: Primary Cyan Photon along Diamond Frame Loop (decelerated: 0.18 -> 0.108 px/ms)
  // Segments: bb->seatJunction (321.9px), seatJunction->headUpper (187.7px), headUpper->headLower (96.5px), headLower->bb (303.3px)
  const dProg = (now * 0.108) % 909.4;
  let px = bb.x, py = bb.y;
  if (dProg < 321.9) {
    const t = dProg / 321.9;
    px = bb.x + (seatJunction.x - bb.x) * t;
    py = bb.y + (seatJunction.y - bb.y) * t;
  } else if (dProg < 321.9 + 187.7) {
    const t = (dProg - 321.9) / 187.7;
    px = seatJunction.x + (headUpper.x - seatJunction.x) * t;
    py = seatJunction.y + (headUpper.y - seatJunction.y) * t;
  } else if (dProg < 321.9 + 187.7 + 96.5) {
    const t = (dProg - 509.6) / 96.5;
    px = headUpper.x + (headLower.x - headUpper.x) * t;
    py = headUpper.y + (headLower.y - headUpper.y) * t;
  } else {
    const t = (dProg - 606.1) / 303.3;
    px = headLower.x + (bb.x - headLower.x) * t;
    py = headLower.y + (bb.y - headLower.y) * t;
  }
  renderPhotonHalo(px, py, 56, 189, 248, 0.9, 16);

  // Photon 2: Amber Photon along Rear Triangle (decelerated: 0.14 -> 0.084 px/ms)
  // Segments: bb->rearDropout (153.3px), rearDropout->seatJunction (294.8px), seatJunction->bb (321.9px)
  const rProg = ((now + 800) * 0.084) % 770.0;
  let rx = bb.x, ry = bb.y;
  if (rProg < 153.3) {
    const t = rProg / 153.3;
    rx = bb.x + (rearDropout.x - bb.x) * t;
    ry = bb.y + (rearDropout.y - bb.y) * t;
  } else if (rProg < 153.3 + 294.8) {
    const t = (rProg - 153.3) / 294.8;
    rx = rearDropout.x + (seatJunction.x - rearDropout.x) * t;
    ry = rearDropout.y + (seatJunction.y - rearDropout.y) * t;
  } else {
    const t = (rProg - 448.1) / 321.9;
    rx = seatJunction.x + (bb.x - seatJunction.x) * t;
    ry = seatJunction.y + (bb.y - seatJunction.y) * t;
  }
  renderPhotonHalo(rx, ry, 251, 191, 36, 0.85, 14);

  // Photon 3: Fork Pulse down to Fork Dropout (cycle extended to 4000ms)
  const fCycle = 4000;
  const fProg = (now % fCycle) / fCycle;
  if (fProg < 0.65) {
    const t = fProg / 0.65;
    const fx = headLower.x + (forkDropout.x - headLower.x) * t;
    const fy = headLower.y + (forkDropout.y - headLower.y) * t;
    const fAlpha = Math.sin(t * Math.PI) * 0.85;
    renderPhotonHalo(fx, fy, 56, 189, 248, fAlpha, 14);
  }

  ctx.restore();
  ctx.restore();
}

/**
 * Official Specialized "AETHOS" Classical Roman Serif Wordmark
 * Derived from authentic carbon frame badge photographic reference.
 * Features true classical AE (Ash) ligature, unbroken continuous horizontal midline strike,
 * sculpted Roman serifs with triangular drop beaks, and vertical stress curvature.
 * Total span: X [-74, 97], Y [-10, 10]. Cumulative length: 538.66px.
 * Format: [x1, y1, x2, y2, len]
 */
export const AETHOS_SERIF_STROKES = [
  // --- 1. CONTINUOUS HORIZONTAL MIDLINE STRIKE ---
  [-72.00,   0.00, -56.00,   0.00, 16.000],
  [-56.00,   0.00, -52.00,   0.00,  4.000],
  [-52.00,   0.00, -38.00,   0.00, 14.000],
  [-38.00,   0.00, -25.00,   0.00, 13.000],
  [-25.00,   0.00, -12.00,   0.00, 13.000],
  [-12.00,   0.00,   1.00,   0.00, 13.000],
  [  1.00,   0.00,  13.00,   0.00, 12.000],
  [ 13.00,   0.00,  33.00,   0.00, 20.000],
  [ 33.00,   0.00,  40.50,   0.00,  7.500],
  [ 40.50,   0.00,  51.00,   0.00, 10.500],
  [ 51.00,   0.00,  61.50,   0.00, 10.500],
  [ 61.50,   0.00,  75.00,   0.00, 13.500],

  // --- 2. AE LIGATURE (Classical Ash Roman) ---
  [-70.00,  10.00, -56.00, -10.00, 24.413],
  [-56.00, -10.00, -52.00, -10.00,  4.000],
  [-52.00, -10.00, -52.00,  10.00, 20.000],
  [-74.00,  10.00, -66.00,  10.00,  8.000],
  [-52.00, -10.00, -38.00, -10.00, 14.000],
  [-38.00, -10.00, -38.00,  -6.00,  4.000],
  [-52.00,  10.00, -37.00,  10.00, 15.000],
  [-37.00,  10.00, -37.00,   6.00,  4.000],

  // --- 3. LETTER T (Wide crossbar, bilateral drop beaks, foot serif) ---
  [-25.00, -10.00,   1.00, -10.00, 26.000],
  [-25.00, -10.00, -25.00,  -6.00,  4.000],
  [  1.00, -10.00,   1.00,  -6.00,  4.000],
  [-12.00, -10.00, -12.00,  10.00, 20.000],
  [-17.00,  10.00,  -7.00,  10.00, 10.000],

  // --- 4. LETTER H (Slender vertical stems, bracketed serifs) ---
  [ 13.00, -10.00,  13.00,  10.00, 20.000],
  [  9.00, -10.00,  17.00, -10.00,  8.000],
  [  9.00,  10.00,  17.00,  10.00,  8.000],
  [ 33.00, -10.00,  33.00,  10.00, 20.000],
  [ 29.00, -10.00,  37.00, -10.00,  8.000],
  [ 29.00,  10.00,  37.00,  10.00,  8.000],

  // --- 5. LETTER O (Classical Roman circle/ellipse Center X=51, Rx=10.5, Ry=10.0) ---
  [ 51.00, -10.00,  53.72,  -9.66,  2.741],
  [ 53.72,  -9.66,  56.25,  -8.66,  2.720],
  [ 56.25,  -8.66,  58.42,  -7.07,  2.690],
  [ 58.42,  -7.07,  60.09,  -5.00,  2.660],
  [ 60.09,  -5.00,  61.14,  -2.59,  2.629],
  [ 61.14,  -2.59,  61.50,   0.00,  2.615],
  [ 61.50,   0.00,  61.14,   2.59,  2.615],
  [ 61.14,   2.59,  60.09,   5.00,  2.629],
  [ 60.09,   5.00,  58.42,   7.07,  2.660],
  [ 58.42,   7.07,  56.25,   8.66,  2.690],
  [ 56.25,   8.66,  53.72,   9.66,  2.720],
  [ 53.72,   9.66,  51.00,  10.00,  2.741],
  [ 51.00,  10.00,  48.28,   9.66,  2.741],
  [ 48.28,   9.66,  45.75,   8.66,  2.720],
  [ 45.75,   8.66,  43.58,   7.07,  2.690],
  [ 43.58,   7.07,  41.91,   5.00,  2.660],
  [ 41.91,   5.00,  40.86,   2.59,  2.629],
  [ 40.86,   2.59,  40.50,   0.00,  2.615],
  [ 40.50,   0.00,  40.86,  -2.59,  2.615],
  [ 40.86,  -2.59,  41.91,  -5.00,  2.629],
  [ 41.91,  -5.00,  43.58,  -7.07,  2.660],
  [ 43.58,  -7.07,  45.75,  -8.66,  2.690],
  [ 45.75,  -8.66,  48.28,  -9.66,  2.720],
  [ 48.28,  -9.66,  51.00, -10.00,  2.741],

  // --- 6. LETTER S (Upright, gracefully curved classical Roman S Center X=75) ---
  [ 82.42,  -6.55,  81.06,  -8.15,  2.100],
  [ 81.06,  -8.15,  78.90,  -9.33,  2.461],
  [ 78.90,  -9.33,  76.22,  -9.94,  2.749],
  [ 76.22,  -9.94,  73.38,  -9.89,  2.840],
  [ 73.38,  -9.89,  70.75,  -9.19,  2.722],
  [ 70.75,  -9.19,  68.69,  -7.94,  2.410],
  [ 68.69,  -7.94,  67.47,  -6.29,  2.052],
  [ 67.47,  -6.29,  67.24,  -4.48,  1.825],
  [ 67.24,  -4.48,  68.05,  -2.73,  1.928],
  [ 68.05,  -2.73,  69.78,  -1.28,  2.257],
  [ 69.78,  -1.28,  72.20,  -0.33,  2.600],
  [ 72.20,  -0.33,  75.00,   0.00,  2.819],
  [ 75.00,   0.00,  77.90,   0.33,  2.919],
  [ 77.90,   0.33,  80.42,   1.28,  2.693],
  [ 80.42,   1.28,  82.22,   2.73,  2.311],
  [ 82.22,   2.73,  83.06,   4.48,  1.941],
  [ 83.06,   4.48,  82.82,   6.29,  1.826],
  [ 82.82,   6.29,  81.55,   7.94,  2.082],
  [ 81.55,   7.94,  79.41,   9.19,  2.478],
  [ 79.41,   9.19,  76.68,   9.89,  2.818],
  [ 76.68,   9.89,  73.73,   9.94,  2.950],
  [ 73.73,   9.94,  70.95,   9.33,  2.846],
  [ 70.95,   9.33,  68.71,   8.15,  2.532],
  [ 68.71,   8.15,  67.30,   6.55,  2.133],
];

export const AETHOS_TOTAL_PATH_LEN = 498.93;

/**
 * Render photonic laser spark & micro-rays at current tracing tip in champagne gold
 */
function renderAethosLaserSpark(px, py, now) {
  if (!ctx) return;
  const grad = ctx.createRadialGradient(px, py, 0, px, py, 11);
  grad.addColorStop(0, 'rgba(255, 255, 255, 0.98)');
  grad.addColorStop(0.35, 'rgba(253, 224, 71, 0.90)');
  grad.addColorStop(0.70, 'rgba(245, 158, 11, 0.50)');
  grad.addColorStop(1, 'rgba(245, 158, 11, 0)');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(px, py, 11, 0, Math.PI * 2);
  ctx.fill();

  const sparkAngle = (now * 0.012) % (Math.PI * 2);
  ctx.strokeStyle = 'rgba(254, 240, 138, 0.90)';
  ctx.lineWidth = 1.1;
  ctx.beginPath();
  for (let k = 0; k < 4; k++) {
    const a = sparkAngle + (k * Math.PI * 0.5);
    const r1 = 2.0;
    const r2 = 6.0 + Math.sin(now * 0.03 + k) * 2.5;
    ctx.moveTo(px + Math.cos(a) * r1, py + Math.sin(a) * r1);
    ctx.lineTo(px + Math.cos(a) * r2, py + Math.sin(a) * r2);
  }
  ctx.stroke();
}

/**
 * Compute screen-space anchor and scale for Specialized Aethos wordmark
 * Guarantees zero viewport truncation and centers within the right flank margin on desktop
 * or the top canopy margin on mobile/narrow screens.
 */
export function computeAethosAnchor(viewportW = 1024, viewportH = 559) {
  const vw = viewportW || 1024;
  const vh = viewportH || 559;

  // Central card boundary from style.css (.main-container max-width: 1080px centered)
  const maxContentW = 1080;
  const contentW = Math.min(vw, maxContentW);
  const contentRight = (vw + contentW) * 0.5;
  const rightMarginW = vw - contentRight;

  // Aethos stroke bounds: minX = -74, maxX = +83.06 -> width = 157.06px, center offset = +4.53px
  let anchorX, anchorY, wordScale;

  if (rightMarginW >= 70) {
    // Desktop: 30% enlarged scale (0.78 * 1.30 = 1.014) and optically centered in right margin
    const marginCenter = (contentRight + vw) * 0.5;
    wordScale = Math.min(1.02, Math.max(0.62, ((rightMarginW - 15) / 150) * 1.30));
    // Shift left to offset visual weight of AE ligature, browser scrollbar, and blueprint drift
    const visualCenterShift = 32 * (wordScale / 0.78);
    anchorX = marginCenter - (4.53 * wordScale) - visualCenterShift;
    // Hard clamp to prevent right-edge clipping under any viewport width or parallax drift
    const maxSafeAnchorX = vw - (83.06 * wordScale) - 45;
    if (anchorX > maxSafeAnchorX) {
      anchorX = maxSafeAnchorX;
    }
    // Positioned cleanly below 48px header with generous breathing room (y ≈ 135px)
    anchorY = Math.max(120, Math.floor(135 * wordScale));
  } else {
    // Mobile / Tablet Canopy Mode: Center in upper margin with 30% scale boost
    wordScale = (vw < 480) ? 0.68 : 0.80;
    anchorX = (vw * 0.5) - (4.53 * wordScale);
    anchorY = 65;
  }

  return { anchorX, anchorY, wordScale, rightMarginW };
}

// Thermal Convective Amber Ember Particle Simulation for Aethos Super-Glow (1.5s window)
export const AETHOS_EMBER_POOL_SIZE = 48;
export const aethosEmbers = new Array(AETHOS_EMBER_POOL_SIZE).fill(null).map(() => ({
  active: false,
  x: 0,
  y: 0,
  spawnX: 0,
  spawnY: 0,
  birthTime: 0,
  lifeMs: 1000,
  vy: -35,
  size: 1.6,
  w1: 3.5,
  w2: 10.0,
  p1: 0,
  p2: 0,
  a1: 4.0,
  a2: 1.8,
  vDrift: 0
}));

let lastAethosSpawnNow = 0;

/**
 * Draw Official Specialized Aethos Classical Roman Serif Laser Tracing
 * Follows a deterministic 9.0s multi-phase cycle in metallic champagne gold.
 * Includes a 1.5s Incandescent Super-Glow Flare (tau: 0.500 -> 0.667) with buoyant amber embers.
 */
export function drawAethosLaserTrace(now, bounds) {
  if (!ctx) return;
  const { anchorX, anchorY, wordScale } = computeAethosAnchor(width, height);

  const cycleMs = 9000;
  const tau = (now % cycleMs) / cycleMs; // 0.0 to 1.0

  if (tau >= 0.85) {
    // Phase 5: Rest / Darkness (tau: 0.85 -> 1.00)
    return;
  }

  ctx.save();
  // Position directly in screen space coordinates (zero blueprint cover distortion)
  ctx.translate(anchorX, anchorY);
  ctx.scale(wordScale, wordScale);

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (tau < 0.35) {
    // Phase 1: Laser Tracing (0.0s -> 3.15s, tau: 0.00 -> 0.35)
    const traceProg = tau / 0.35;
    const targetDist = traceProg * AETHOS_TOTAL_PATH_LEN;

    let accum = 0;
    let tipX = AETHOS_SERIF_STROKES[0][0];
    let tipY = AETHOS_SERIF_STROKES[0][1];

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = 'rgba(251, 191, 36, 0.88)';
    ctx.lineWidth = 1.6;
    ctx.beginPath();

    for (let i = 0; i < AETHOS_SERIF_STROKES.length; i++) {
      const s = AETHOS_SERIF_STROKES[i];
      const sLen = s[4];
      if (accum + sLen <= targetDist) {
        ctx.moveTo(s[0], s[1]);
        ctx.lineTo(s[2], s[3]);
        accum += sLen;
        tipX = s[2];
        tipY = s[3];
      } else if (accum < targetDist) {
        const t = (targetDist - accum) / sLen;
        tipX = s[0] + (s[2] - s[0]) * t;
        tipY = s[1] + (s[3] - s[1]) * t;
        ctx.moveTo(s[0], s[1]);
        ctx.lineTo(tipX, tipY);
        accum = targetDist;
        break;
      } else {
        break;
      }
    }
    ctx.stroke();

    // Laser spark at active drawing tip
    renderAethosLaserSpark(tipX, tipY, now);
    ctx.restore();

  } else if (tau < 0.50) {
    // Phase 2: Energized Bloom (3.15s -> 4.50s, tau: 0.35 -> 0.50)
    const breath = 0.75 + Math.sin(now * 0.007) * 0.25;

    // Solid Specular Underlay Pass for full-glow punch-through
    ctx.save();
    ctx.beginPath();
    for (let i = 0; i < AETHOS_SERIF_STROKES.length; i++) {
      const s = AETHOS_SERIF_STROKES[i];
      ctx.moveTo(s[0], s[1]);
      ctx.lineTo(s[2], s[3]);
    }
    ctx.strokeStyle = `rgba(255, 255, 255, ${0.70 * breath})`;
    ctx.lineWidth = 1.0;
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.globalCompositeOperation = 'screen';

    // Pass 1: Volumetric Warm Amber/Gold Bloom (-20% stroke width)
    ctx.beginPath();
    for (let i = 0; i < AETHOS_SERIF_STROKES.length; i++) {
      const s = AETHOS_SERIF_STROKES[i];
      ctx.moveTo(s[0], s[1]);
      ctx.lineTo(s[2], s[3]);
    }
    ctx.strokeStyle = `rgba(245, 158, 11, ${0.45 * breath})`;
    ctx.lineWidth = 4.0;
    ctx.stroke();

    // Pass 2: High-Intensity Champagne Core (-20% stroke width)
    ctx.strokeStyle = `rgba(253, 224, 71, ${0.90 * breath})`;
    ctx.lineWidth = 1.9;
    ctx.stroke();

    // Pass 3: Specular White-Gold Centerline (-20% stroke width)
    ctx.strokeStyle = `rgba(255, 255, 255, ${0.95 * breath})`;
    ctx.lineWidth = 1.0;
    ctx.stroke();

    ctx.restore();

  } else if (tau < 0.667) {
    // Phase 3: Pre-Dissipation Super-Glow Flare & Thermal Amber Emitter (4.50s -> 6.00s, exactly 1.50s)
    const u = (tau - 0.500) / 0.1667; // 0.0 to 1.0
    const surge = Math.pow(Math.sin(u * Math.PI), 0.75); // Sustained incandescent bell curve

    // 1. Spawning thermal embers during flare ramp & sustain
    if (surge > 0.08 && now - lastAethosSpawnNow > (36 / (0.3 + 0.7 * surge))) {
      lastAethosSpawnNow = now;
      for (let k = 0; k < AETHOS_EMBER_POOL_SIZE; k++) {
        const p = aethosEmbers[k];
        if (!p.active) {
          const s = AETHOS_SERIF_STROKES[Math.floor(Math.random() * AETHOS_SERIF_STROKES.length)];
          const t = Math.random();
          p.active = true;
          p.spawnX = s[0] + (s[2] - s[0]) * t;
          p.spawnY = s[1] + (s[3] - s[1]) * t;
          p.x = p.spawnX;
          p.y = p.spawnY;
          p.birthTime = now;
          p.lifeMs = 700 + Math.random() * 600; // 0.7s to 1.3s
          p.vy = -30 - Math.random() * 28; // -30 to -58 px/s buoyant upward velocity
          p.size = 1.2 + Math.random() * 1.5;
          p.w1 = 2.4 + Math.random() * 2.0; // Macro plume sway
          p.w2 = 7.5 + Math.random() * 6.5; // Micro turbulent flutter
          p.p1 = Math.random() * Math.PI * 2;
          p.p2 = Math.random() * Math.PI * 2;
          p.a1 = 2.5 + Math.random() * 3.5;
          p.a2 = 0.8 + Math.random() * 1.8;
          p.vDrift = (Math.random() - 0.5) * 6.0;
          break;
        }
      }
    }

    // Solid Specular Underlay Pass for 100% Vignette Penetration & Crisp Letterform Definition
    ctx.save();
    ctx.beginPath();
    for (let i = 0; i < AETHOS_SERIF_STROKES.length; i++) {
      const s = AETHOS_SERIF_STROKES[i];
      ctx.moveTo(s[0], s[1]);
      ctx.lineTo(s[2], s[3]);
    }
    ctx.strokeStyle = `rgba(255, 255, 255, ${0.85 + 0.15 * surge})`;
    ctx.lineWidth = 1.0 + 0.8 * surge;
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.globalCompositeOperation = 'screen';

    // Pass 1: Volumetric Solar Corona Aura (-20% stroke width: 4.0px to 8.8px)
    ctx.beginPath();
    for (let i = 0; i < AETHOS_SERIF_STROKES.length; i++) {
      const s = AETHOS_SERIF_STROKES[i];
      ctx.moveTo(s[0], s[1]);
      ctx.lineTo(s[2], s[3]);
    }
    ctx.strokeStyle = `rgba(245, 158, 11, ${0.50 + 0.45 * surge})`;
    ctx.lineWidth = 4.0 + 4.8 * surge;
    ctx.stroke();

    // Pass 2: Hyper-Intense Radiant Champagne Core (-20% stroke width: 1.9px to 4.3px)
    ctx.strokeStyle = `rgba(253, 224, 71, ${0.90 + 0.10 * surge})`;
    ctx.lineWidth = 1.9 + 2.4 * surge;
    ctx.stroke();

    // Pass 3: Incandescent Specular White Core (-20% stroke width: 1.0px to 2.25px)
    ctx.strokeStyle = `rgba(255, 255, 255, ${0.95 + 0.05 * surge})`;
    ctx.lineWidth = 1.0 + 1.25 * surge;
    ctx.stroke();

    ctx.restore();

    // 2. Render & simulate active rising thermal embers with natural turbulence
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    for (let k = 0; k < AETHOS_EMBER_POOL_SIZE; k++) {
      const p = aethosEmbers[k];
      if (!p.active) continue;
      const age = (now - p.birthTime) / 1000;
      const progress = (now - p.birthTime) / p.lifeMs;
      if (progress >= 1.0) {
        p.active = false;
        continue;
      }

      // Buoyant vertical rise with heat convection acceleration
      p.y = p.spawnY + p.vy * age - 0.5 * 16 * age * age;
      // Natural dual-harmonic lateral flutter
      const flutter = p.a1 * Math.sin(p.w1 * age + p.p1) + 
                      p.a2 * Math.cos(p.w2 * age + p.p2) + 
                      p.vDrift * age;
      p.x = p.spawnX + flutter;

      // Thermal scintillation & cooling
      const flicker = 0.85 + 0.15 * Math.sin(age * 22);
      const alpha = Math.sin(progress * Math.PI) * flicker;
      const curRadius = Math.max(0.4, p.size * (1.0 - 0.55 * progress));

      // Blackbody thermal gradient: Solar Gold (254, 240, 138) -> Amber (245, 158, 11) -> Ember Flame (217, 119, 6)
      const r = 252;
      const g = Math.floor(220 - 110 * progress);
      const b = Math.floor(120 * (1.0 - progress));

      ctx.beginPath();
      ctx.arc(p.x, p.y, curRadius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha * 0.9})`;
      ctx.shadowColor = 'rgba(245, 158, 11, 0.8)';
      ctx.shadowBlur = 4 * (1.0 - progress);
      ctx.fill();
    }
    ctx.restore();

  } else {
    // Phase 4: Dissipation (6.00s -> 7.65s, tau: 0.667 -> 0.85)
    const fadeProg = (tau - 0.667) / 0.183;
    const alphaDecay = 0.5 * (1 + Math.cos(fadeProg * Math.PI));

    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.beginPath();
    for (let i = 0; i < AETHOS_SERIF_STROKES.length; i++) {
      const s = AETHOS_SERIF_STROKES[i];
      ctx.moveTo(s[0], s[1]);
      ctx.lineTo(s[2], s[3]);
    }
    ctx.strokeStyle = `rgba(251, 191, 36, ${0.75 * alphaDecay})`;
    ctx.lineWidth = 1.2 + 1.8 * alphaDecay;
    ctx.stroke();
    ctx.restore();
  }

  ctx.restore();
}

/**
 * Draw Luminescent Hub & Bottom Bracket Bearing Nodes
 */
export function drawHubGlowNodes(now, bounds) {
  if (!ctx) return;
  const b = bounds || computeCoverBounds(width, height, 1024, 559);
  const { bb, rearDropout, forkDropout } = BLUEPRINT_COORDS;

  ctx.save();
  ctx.translate(b.offsetX, b.offsetY);
  ctx.scale(b.scale, b.scale);

  const nodes = [
    { pt: bb, color: '56, 189, 248', phase: 0 },
    { pt: rearDropout, color: '56, 189, 248', phase: 1.2 },
    { pt: forkDropout, color: '56, 189, 248', phase: 2.4 }
  ];

  ctx.save();
  ctx.globalCompositeOperation = 'screen';

  nodes.forEach(({ pt, color, phase }) => {
    const pulse = 0.30 + Math.sin(now * 0.0028 + phase) * 0.18;
    const grad = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, 16);
    grad.addColorStop(0, `rgba(${color}, ${pulse})`);
    grad.addColorStop(0.5, `rgba(${color}, ${pulse * 0.35})`);
    grad.addColorStop(1, `rgba(${color}, 0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, 16, 0, Math.PI * 2);
    ctx.fill();

    // Subtle technical crosshair ticks
    ctx.strokeStyle = `rgba(${color}, ${pulse * 0.6})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pt.x - 6, pt.y);
    ctx.lineTo(pt.x + 6, pt.y);
    ctx.moveTo(pt.x, pt.y - 6);
    ctx.lineTo(pt.x, pt.y + 6);
    ctx.stroke();
  });

  ctx.restore();
  ctx.restore();
}

/**
 * Draw Interactive UV Blacklight Flashlight (Phosphor reveal effect beneath cursor)
 */
export function drawInteractiveUvFlashlight() {
  if (!ctx || mouseCurrentAlpha < 0.01) return;
  if (mouseX < -50 || mouseY < -50) return;

  ctx.save();
  ctx.globalCompositeOperation = 'screen';

  const flashRadius = 180;
  const grad = ctx.createRadialGradient(mouseX, mouseY, 0, mouseX, mouseY, flashRadius);
  grad.addColorStop(0, `rgba(56, 189, 248, ${mouseCurrentAlpha * 0.28})`);
  grad.addColorStop(0.45, `rgba(147, 51, 234, ${mouseCurrentAlpha * 0.09})`); // UV phosphor violet tint
  grad.addColorStop(1, 'rgba(56, 189, 248, 0)');

  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(mouseX, mouseY, flashRadius, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

/**
 * Draw traveling energy packet along the exploded hub assembly vector
 */
export function drawExplodedAxlePulse(now, bounds) {
  if (!ctx) return;
  const b = bounds || computeCoverBounds(width, height, 1024, 559);
  const startX = b.offsetX + BLUEPRINT_COORDS.explodedHubStart.x * b.scale;
  const startY = b.offsetY + BLUEPRINT_COORDS.explodedHubStart.y * b.scale;
  const endX = b.offsetX + BLUEPRINT_COORDS.explodedHubEnd.x * b.scale;
  const endY = b.offsetY + BLUEPRINT_COORDS.explodedHubEnd.y * b.scale;

  const cycleMs = 3800;
  const progress = (now % cycleMs) / cycleMs; // 0.0 to 1.0

  const curX = startX + (endX - startX) * progress;
  const curY = startY + (endY - startY) * progress;
  const alpha = Math.sin(progress * Math.PI) * 0.75;

  ctx.save();
  ctx.globalCompositeOperation = 'lighter';

  // Soft glowing halo
  const haloGrad = ctx.createRadialGradient(curX, curY, 0, curX, curY, 18);
  haloGrad.addColorStop(0, `rgba(56, 189, 248, ${alpha})`);
  haloGrad.addColorStop(0.4, `rgba(251, 191, 36, ${alpha * 0.6})`);
  haloGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');
  ctx.fillStyle = haloGrad;
  ctx.beginPath();
  ctx.arc(curX, curY, 18, 0, Math.PI * 2);
  ctx.fill();

  // Core photon
  ctx.beginPath();
  ctx.arc(curX, curY, 2.5, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
  ctx.fill();

  // Trail streak
  const trailLen = 32;
  const dirX = (startX - endX);
  const dirY = (startY - endY);
  const mag = Math.hypot(dirX, dirY) || 1;
  const ndx = dirX / mag;
  const ndy = dirY / mag;

  ctx.beginPath();
  ctx.moveTo(curX, curY);
  ctx.lineTo(curX + ndx * trailLen * progress, curY + ndy * trailLen * progress);
  ctx.strokeStyle = `rgba(56, 189, 248, ${alpha * 0.5})`;
  ctx.lineWidth = 1.6;
  ctx.stroke();

  ctx.restore();
}

/**
 * Draw ambient cellular shimmer over the Drivetrain Optimization matrix
 */
export function drawMatrixShimmer(now, bounds) {
  if (!ctx) return;
  const b = bounds || computeCoverBounds(width, height, 1024, 559);
  const matX = b.offsetX + BLUEPRINT_COORDS.matrix.x * b.scale;
  const matY = b.offsetY + BLUEPRINT_COORDS.matrix.y * b.scale;
  const matW = BLUEPRINT_COORDS.matrix.w * b.scale;
  const matH = BLUEPRINT_COORDS.matrix.h * b.scale;

  if (matW < 80 || matH < 40) return;

  const pulse = 0.05 + Math.sin(now * 0.0016) * 0.035;

  ctx.save();
  ctx.globalCompositeOperation = 'screen';

  // Ambient back-glow
  const bgGrad = ctx.createRadialGradient(matX + matW * 0.5, matY + matH * 0.5, 0, matX + matW * 0.5, matY + matH * 0.5, matW * 0.6);
  bgGrad.addColorStop(0, `rgba(56, 189, 248, ${pulse})`);
  bgGrad.addColorStop(0.7, `rgba(56, 189, 248, ${pulse * 0.4})`);
  bgGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(matX, matY, matW, matH);

  // Subtle CAD telemetry scan cursor
  const scanX = matX + ((now * 0.04) % matW);
  ctx.beginPath();
  ctx.moveTo(scanX, matY);
  ctx.lineTo(scanX, matY + matH);
  ctx.strokeStyle = `rgba(56, 189, 248, ${pulse * 1.5})`;
  ctx.lineWidth = 1;
  ctx.setLineDash([2, 2]);
  ctx.stroke();

  ctx.restore();
}

/**
 * Draw animated CAD laser scanning sweep
 */
function drawLaserScan(now) {
  // Smooth sine sweep across viewport
  const scanY = height * (0.5 + 0.46 * Math.sin(now * 0.00055));
  ctx.save();
  // Faint wide laser glow
  const glowGrad = ctx.createLinearGradient(0, scanY - 14, 0, scanY + 14);
  glowGrad.addColorStop(0, 'rgba(56, 189, 248, 0)');
  glowGrad.addColorStop(0.5, 'rgba(56, 189, 248, 0.12)');
  glowGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');
  ctx.fillStyle = glowGrad;
  ctx.fillRect(0, scanY - 14, width, 28);

  // Sharp laser beam core
  ctx.beginPath();
  ctx.strokeStyle = 'rgba(56, 189, 248, 0.45)';
  ctx.lineWidth = 1.2;
  ctx.moveTo(0, scanY);
  ctx.lineTo(width, scanY);
  ctx.stroke();

  // CAD readout telemetry marker
  ctx.font = '10px monospace';
  ctx.fillStyle = 'rgba(56, 189, 248, 0.45)';
  ctx.fillText(`CAD SCAN // ELEV: ${(scanY * 0.18).toFixed(1)}mm`, 24, scanY - 6);
  ctx.restore();
}

/**
 * Draw concentric bearing ripple rings over technical drafting zones
 */
function drawBearingRipples(now) {
  const hubX = width * 0.65;
  const hubY = height * 0.28;
  const maxR = 140;

  ctx.save();
  ctx.lineWidth = 1.2;
  ctx.setLineDash([4, 4]);

  for (let i = 0; i < 3; i++) {
    const r = ((now * 0.035 + i * 45) % maxR) + 10;
    const alpha = (1 - (r / (maxR + 10))) * 0.25;
    ctx.strokeStyle = `rgba(56, 189, 248, ${alpha})`;
    ctx.beginPath();
    ctx.arc(hubX, hubY, r, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Hub crosshair mark
  ctx.setLineDash([]);
  ctx.strokeStyle = 'rgba(56, 189, 248, 0.22)';
  ctx.beginPath();
  ctx.moveTo(hubX - 8, hubY);
  ctx.lineTo(hubX + 8, hubY);
  ctx.moveTo(hubX, hubY - 8);
  ctx.lineTo(hubX, hubY + 8);
  ctx.stroke();

  ctx.restore();
}

/**
 * Draw interactive CAD crosshair reticle following cursor
 */
function drawHudCrosshair() {
  mouseCurrentAlpha += (mouseTargetAlpha - mouseCurrentAlpha) * 0.08;
  if (mouseCurrentAlpha < 0.01) return;

  ctx.save();
  ctx.globalAlpha = mouseCurrentAlpha;
  ctx.strokeStyle = 'rgba(56, 189, 248, 0.65)';
  ctx.fillStyle = 'rgba(56, 189, 248, 0.55)';
  ctx.lineWidth = 1;

  // Outer reticle ring
  ctx.beginPath();
  ctx.arc(mouseX, mouseY, 22, 0, Math.PI * 2);
  ctx.stroke();

  // Center dot
  ctx.beginPath();
  ctx.arc(mouseX, mouseY, 2, 0, Math.PI * 2);
  ctx.fill();

  // Cross lines
  ctx.beginPath();
  ctx.moveTo(mouseX - 32, mouseY);
  ctx.lineTo(mouseX - 26, mouseY);
  ctx.moveTo(mouseX + 26, mouseY);
  ctx.lineTo(mouseX + 32, mouseY);
  ctx.moveTo(mouseX, mouseY - 32);
  ctx.lineTo(mouseX, mouseY - 26);
  ctx.moveTo(mouseX, mouseY + 26);
  ctx.lineTo(mouseX, mouseY + 32);
  ctx.stroke();

  // Coordinate text readout
  ctx.font = '9px monospace';
  ctx.fillText(`X:${clientMouseX.toFixed(0)} Y:${clientMouseY.toFixed(0)}`, mouseX + 10, mouseY + 18);

  ctx.restore();
}

/**
 * Pre-allocated static buffers for Zero-Allocation Hot Loop in Performance Dashboard
 */
const pdcRawPowers = new Float32Array(8);
const pdcScreenCoords = new Float32Array(8 * 2);
const pdcTangents = new Float32Array(8);
const pdcSecants = new Float32Array(7);
const vo2UpCoords = new Float32Array(6 * 2);
const vo2DnCoords = new Float32Array(6 * 2);
const vo2MedCoords = new Float32Array(6 * 2);
const VO2_MED = new Float32Array([78.2, 80.1, 82.4, 84.0, 85.5, 86.4]);
const VO2_UP = new Float32Array([80.6, 82.5, 84.8, 86.4, 87.9, 88.8]);
const VO2_DN = new Float32Array([76.2, 78.1, 80.4, 82.0, 83.5, 84.4]);
const HUD_LABELS = ['PWR', 'HR', 'CAD', 'W/KG', 'SPD', 'KJ'];
const HUD_COLORS = ['#38bdf8', '#fb7185', '#4ade80', '#fde047', '#67e8f9', '#ffffff'];
const HUD_RGB = [
  [56, 189, 248],   // PWR: Electric Cyan
  [251, 113, 133],  // HR: Rose
  [74, 222, 128],   // CAD: Emerald
  [253, 224, 71],   // W/KG: Amber Gold
  [103, 232, 249],  // SPD: Aero Cyan
  [255, 255, 255]   // KJ: Platinum White
];
const HUD_PAIRS = [[0, 3], [1, 4], [2, 4], [0, 5], [1, 2]];

/**
 * Compute screen-space bounding box for Right-Margin Performance Telemetry Dashboard
 * Guarantees safe desktop-only clearance outside central feed (.main-container max-width: 1080px),
 * dynamic proportional scaling under browser zoom (100% to 150%), clean vertical decoupling
 * below the Specialized AETHOS wordmark, and robust anti-drift right-edge protection.
 */
export function computeDashboardBounds(viewportW = 1024, viewportH = 559) {
  const vw = viewportW || 1024;
  const vh = viewportH || 559;

  // Viewport width in screen space (canvas has inset: -25px, width: calc(100vw + 50px))
  const screenW = Math.max(320, vw - 50);
  const maxContentW = 1080;
  const contentW = Math.min(screenW, maxContentW);
  const screenContentRight = (screenW + contentW) * 0.5;
  const rightMarginW = screenW - screenContentRight;

  // Viewport gate: desktop layout only with >= 120px right margin and screen width >= 1180
  if (rightMarginW < 120 || screenW < 1180) {
    return null;
  }

  // Dynamic zoom scaling factor (dashScale): scales smoothly down from 1.0 to 0.55
  // when available margin shrinks under browser zoom (100% to 150%)
  const panelW = Math.floor(Math.min(280, Math.max(150, rightMarginW - 24)));
  const dashScale = Math.min(1.0, Math.max(0.55, panelW / 280));

  // Canvas coordinate projection: canvas starts at -25px, so canvasX = screenX + 25px
  // 38px clearance buffer protects against scrollbar gutter (17px), drift (3px), and parallax (8px)
  const canvasContentRight = screenContentRight + 25;
  const canvasScreenRight = screenW + 25 - 38;

  // Optical horizontal centering within right margin with inward tuck
  const marginCenter = (canvasContentRight + canvasScreenRight) * 0.5;
  const visualInwardShift = Math.floor(14 * dashScale);
  let panelX = Math.floor(marginCenter - (panelW * 0.5) - visualInwardShift);

  // Hard boundary clamping guardrails: zero feed overlap and zero right-edge clipping
  const minSafeX = Math.floor(canvasContentRight + 8);
  const maxSafeX = Math.floor(canvasScreenRight - panelW - 6);
  panelX = Math.max(minSafeX, Math.min(panelX, maxSafeX));

  // Vertical placement: top edge of 1st panel aligns with Club Media Center frame (y ≈ 205px)
  const panelY = Math.max(180, Math.floor(205 * dashScale));

  // Dynamic panel height scaled to contain all 4 spaced modules (including 76px HUD strip)
  const panelH = Math.floor(520 * dashScale);

  if (vh < panelY + Math.floor(280 * dashScale)) {
    return null; // Not enough vertical clearance
  }

  return { x: panelX, y: panelY, w: panelW, h: panelH, scale: dashScale, rightMarginW };
}

/**
 * Pre-allocated Alpha Buffer for 4 Detached Panels (Zero GC)
 * Baseline resting opacity: 0.72, Hovered opacity: 1.00
 */
export const panelHoverAlphas = new Float32Array([0.72, 0.72, 0.72, 0.72]);
let activeHoveredPanelIdx = -1;

/**
 * Axis-Aligned Bounding Box (AABB) hit tester for the 4 detached metric panels.
 * Note: All 6 metric boxes in the Telemetry Strip are treated as 1 single panel (Index 3).
 * Returns: 0 (PDC), 1 (VO2 Max), 2 (HR vs PWR), 3 (Telemetry Strip), or -1 (none/gap).
 */
export function getHoveredPanelIndex(bounds, mx, my) {
  if (!bounds || mx < 0 || my < 0) return -1;

  // Panel 0: PDC Graph Widget
  const pdc = computePdcBounds(bounds);
  if (mx >= pdc.modX && mx <= pdc.modX + pdc.modW && my >= pdc.modY && my <= pdc.modY + pdc.modH) {
    return 0;
  }

  // Panel 1: VO2 Max Profile Widget
  const vo2 = computeVo2Bounds(bounds);
  if (mx >= vo2.modX && mx <= vo2.modX + vo2.modW && my >= vo2.modY && my <= vo2.modY + vo2.modH) {
    return 1;
  }

  // Panel 2: HR vs PWR Scatter Widget
  const hrPwr = computeHrPwrBounds(bounds);
  if (mx >= hrPwr.modX && mx <= hrPwr.modX + hrPwr.modW && my >= hrPwr.modY && my <= hrPwr.modY + hrPwr.modH) {
    return 2;
  }

  // Panel 3: Telemetry Matrix Strip (all 6 metric boxes treated as 1 single panel)
  const telem = computeTelemetryStripBounds(bounds);
  if (mx >= telem.modX && mx <= telem.modX + telem.modW && my >= telem.modY && my <= telem.modY + telem.modH) {
    return 3;
  }

  return -1;
}

/**
 * Update dynamic telemetry metrics and scatter jitter with zero GC allocation
 */
function updateTelemetry(now) {
  // Live power: 285W base with smooth physiological pedal cycle oscillation (268W - 302W)
  const pwr = 285 + Math.sin(now * 0.0018) * 12 + Math.sin(now * 0.0049) * 5;
  telemetryState[0] = pwr;
  // Heart rate: 158 BPM with cardiac cadence drift (154 - 162 BPM)
  telemetryState[1] = 158 + Math.sin(now * 0.0011) * 3.8;
  // Cadence: 92 RPM with smooth pedal stroke fluctuation (90 - 95 RPM)
  telemetryState[2] = 92 + Math.sin(now * 0.0024) * 2.5;
  // Power-to-weight: 69.5 kg rider mass
  telemetryState[3] = pwr / 69.5;
  // Speed: 24.6 MPH coupled to instantaneous power
  telemetryState[4] = 24.6 + (pwr - 285) * 0.032;
  // Accumulated work (KJ): increments slowly over time
  telemetryState[5] = 1420 + (now * 0.00028) % 80;
  // Correlation & efficiency metrics
  telemetryState[6] = 0.94;
  telemetryState[7] = 1.80;

  // Scatter pool live micro-jitter (orbital phase drift for 48 points)
  for (let i = 0; i < SCATTER_COUNT; i++) {
    const offset = i * SCATTER_STRIDE;
    const basePwr = scatterPool[offset + 2];
    const baseHr = scatterPool[offset + 3];
    const phase = scatterPool[offset + 5];

    scatterPool[offset] = basePwr + Math.cos(phase + now * 0.0015) * 4.0;
    scatterPool[offset + 1] = baseHr + Math.sin(phase + now * 0.0018) * 2.2;
  }
}

/**
 * Modular Bounds Computation Helpers for 4 Decoupled Telemetry Components
 * Guarantees completely detached floating islands with 14px transparent gaps exposing blueprint backdrop
 */
export function computePdcBounds(bounds) {
  const { x, y, w, scale = 1.0 } = bounds;
  return {
    modX: x,
    modY: y,
    modW: w,
    modH: Math.floor(130 * scale),
    scale
  };
}

export function computeVo2Bounds(bounds) {
  const { x, y, w, scale = 1.0 } = bounds;
  const gap = Math.floor(14 * scale);
  return {
    modX: x,
    modY: y + Math.floor(130 * scale) + gap,
    modW: w,
    modH: Math.floor(122 * scale),
    scale
  };
}

export function computeHrPwrBounds(bounds) {
  const { x, y, w, scale = 1.0 } = bounds;
  const gap = Math.floor(14 * scale);
  return {
    modX: x,
    modY: y + Math.floor(130 * scale) + gap + Math.floor(122 * scale) + gap,
    modW: w,
    modH: Math.floor(134 * scale),
    scale
  };
}

export function computeTelemetryStripBounds(bounds) {
  const { x, y, w, scale = 1.0 } = bounds;
  const gap = Math.floor(14 * scale);
  return {
    modX: x,
    modY: y + Math.floor(130 * scale) + gap + Math.floor(122 * scale) + gap + Math.floor(134 * scale) + gap,
    modW: w,
    modH: Math.floor(76 * scale),
    scale
  };
}

/**
 * Render technical frame container helper (monolithic outer bounding box retired)
 */
export function drawDashboardContainer(bounds) {
  // Monolithic outer bounding box retired (ctx.fillStyle = '#0a101e') to ensure
  // 100% transparent gaps between detached island frames with blueprint backdrop showing through.
}

/**
 * Constructs a true truncated chamfer polygon path for the PDC card.
 */
export function buildPdcCardPath(targetCtx, x, y, w, h, c) {
  targetCtx.beginPath();
  targetCtx.moveTo(x + c * 2, y);
  targetCtx.lineTo(x + w - c * 2, y);
  targetCtx.lineTo(x + w, y + c * 2);
  targetCtx.lineTo(x + w, y + h - c * 2);
  targetCtx.lineTo(x + w - c * 2, y + h);
  targetCtx.lineTo(x, y + h);
  targetCtx.lineTo(x, y + c * 2);
  targetCtx.closePath();
}

/**
 * Module 1: Power Duration Curve (PDC)
 * Logarithmic time scale (1s -> 5h) with multi-zone energy fill and gliding tracer
 */
function drawPowerDurationModule(bounds, now, alpha = panelHoverAlphas[0]) {
  const { modX, modY, modW, modH, scale = 1.0 } = computePdcBounds(bounds);
  const isHovered = alpha >= 0.85;
  const c = Math.max(3, Math.floor(5 * scale));

  ctx.save();
  // Soft ambient drop shadow with theme-colored luminous bloom on hover
  ctx.shadowColor = isHovered ? 'rgba(56, 189, 248, 0.40)' : 'rgba(0, 0, 0, 0.60)';
  ctx.shadowBlur = Math.floor((isHovered ? 20 : 8) * scale);
  ctx.shadowOffsetY = Math.floor((isHovered ? 3 : 2) * scale);

  // High-Luminance Illuminated Cockpit Backlighting & Surface
  if (isHovered) {
    const cardGrad = ctx.createLinearGradient(modX, modY, modX, modY + modH);
    cardGrad.addColorStop(0, '#1e293b'); // slate-800 illuminated top
    cardGrad.addColorStop(0.45, '#131e32');
    cardGrad.addColorStop(1, '#0b1120'); // deep contrast bottom
    ctx.fillStyle = cardGrad;
  } else {
    ctx.fillStyle = (alpha >= 0.98) ? '#0f172a' : `rgba(15, 23, 42, ${alpha.toFixed(3)})`;
  }
  ctx.strokeStyle = isHovered ? 'rgba(56, 189, 248, 0.95)' : 'rgba(56, 189, 248, 0.28)';
  ctx.lineWidth = isHovered ? 1.6 : 1.0;
  buildPdcCardPath(ctx, modX, modY, modW, modH, c);
  ctx.fill();
  ctx.stroke();

  // Reset shadow for crisp hairline strokes
  ctx.shadowColor = 'transparent';

  if (isHovered) {
    // Ambient inner radial backlight bloom for crisp contrast
    const innerGlow = ctx.createRadialGradient(
      modX + modW * 0.5, modY + modH * 0.45, 10,
      modX + modW * 0.5, modY + modH * 0.45, Math.max(modW, modH) * 0.65
    );
    innerGlow.addColorStop(0, 'rgba(56, 189, 248, 0.16)');
    innerGlow.addColorStop(0.65, 'rgba(56, 189, 248, 0.04)');
    innerGlow.addColorStop(1, 'rgba(56, 189, 248, 0)');
    ctx.fillStyle = innerGlow;
    buildPdcCardPath(ctx, modX, modY, modW, modH, c);
    ctx.fill();
  }

  // Bespoke High-Tech Framing 1: Chamfered Corner Notches & Holographic Specular Shimmer
  ctx.strokeStyle = isHovered ? 'rgba(56, 189, 248, 0.98)' : 'rgba(56, 189, 248, 0.70)';
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  // Top-left notch
  ctx.moveTo(modX, modY + c * 2);
  ctx.lineTo(modX + c * 2, modY);
  ctx.lineTo(modX + Math.floor(16 * scale), modY);
  // Top-right notch
  ctx.moveTo(modX + modW - Math.floor(16 * scale), modY);
  ctx.lineTo(modX + modW - c * 2, modY);
  ctx.lineTo(modX + modW, modY + c * 2);
  // Bottom-right notch
  ctx.moveTo(modX + modW - Math.floor(16 * scale), modY + modH);
  ctx.lineTo(modX + modW - c * 2, modY + modH);
  ctx.lineTo(modX + modW, modY + modH - c * 2);
  ctx.stroke();

  // Premium Holographic Specular Glint & Shimmering Flare (replaces cheap moving dotted line)
  // Measured 5400ms period gliding smoothly along perimeter chamfer
  const glintT = (now * 0.00035) % 1.0;
  const glintSpan = Math.max(45, Math.floor(70 * scale));
  const glintX = modX + glintT * (modW + glintSpan) - glintSpan * 0.5;
  const glintGrad = ctx.createLinearGradient(glintX - glintSpan * 0.5, modY, glintX + glintSpan * 0.5, modY);
  glintGrad.addColorStop(0, 'rgba(56, 189, 248, 0)');
  glintGrad.addColorStop(0.3, 'rgba(192, 132, 252, 0.45)');
  glintGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.95)');
  glintGrad.addColorStop(0.7, 'rgba(56, 189, 248, 0.55)');
  glintGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');

  // Multi-harmonic phosphor shimmer modulation for breathing, non-linear luminescence
  const pPulse = (now * 0.0012) % (Math.PI * 2);
  const plasmaAlpha = 0.52 + 0.30 * Math.sin(pPulse) * Math.cos(now * 0.00073);
  ctx.strokeStyle = isHovered ? glintGrad : `rgba(192, 132, 252, ${(plasmaAlpha * 0.65).toFixed(3)})`;
  ctx.lineWidth = isHovered ? 1.6 : 1.2;
  buildPdcCardPath(ctx, modX, modY, modW, modH, c);
  ctx.stroke();

  if (isHovered) {
    // Secondary hairline specular glint on chamfers
    ctx.strokeStyle = glintGrad;
    ctx.lineWidth = 1.8;
    buildPdcCardPath(ctx, modX, modY, modW, modH, c);
    ctx.stroke();
  }

  // Plot Area
  const padL = Math.floor(26 * scale);
  const padR = Math.floor(8 * scale);
  const padT = Math.floor(20 * scale);
  const padB = Math.floor(18 * scale);
  const plotX = modX + padL;
  const plotY = modY + padT;
  const plotW = Math.max(20, modW - padL - padR);
  const plotH = Math.max(20, modH - padT - padB);

  // 1. Dynamic kinetic morphing (+25% amplitude boost) with pedal surge coupling
  const pwrDelta = telemetryState[0] - 285;
  for (let i = 0; i < 8; i++) {
    const d = PDC_PTS[i];
    // Fluctuation factors boosted by 1.25x (+25% morphing) with dual-harmonic ripple
    const torqueFactor = 1.00 - i * 0.10;
    const wave1 = Math.sin(now * 0.0018 + i * 0.85) * (8.75 - i * 0.75);
    const wave2 = Math.cos(now * 0.0031 + i * 1.20) * (3.50 - i * 0.30);
    const fluc = (pwrDelta * torqueFactor) + wave1 + wave2;
    pdcRawPowers[i] = d.pwr + fluc;
  }

  // 2. Tier 1: Vertex Monotonic Clamping (Enforce strictly non-increasing: P[i] < P[i-1])
  for (let i = 1; i < 8; i++) {
    if (pdcRawPowers[i] >= pdcRawPowers[i - 1]) {
      pdcRawPowers[i] = pdcRawPowers[i - 1] - 3.0; // Enforce minimum separation drop
    }
  }

  // 3. Project into screen coordinates
  for (let i = 0; i < 8; i++) {
    const d = PDC_PTS[i];
    pdcScreenCoords[i * 2] = plotX + d.t * plotW;
    pdcScreenCoords[i * 2 + 1] = plotY + plotH - ((pdcRawPowers[i] - 150) / 1050) * plotH;
  }

  // 4. Tier 2: Fritsch-Carlson Monotone Spline (Analytic proof that slope dP/dx <= 0 everywhere)
  // In screen coords, decreasing power means y increases downward (dy >= 0).
  for (let i = 0; i < 7; i++) {
    const dx = pdcScreenCoords[(i + 1) * 2] - pdcScreenCoords[i * 2];
    const dy = pdcScreenCoords[(i + 1) * 2 + 1] - pdcScreenCoords[i * 2 + 1];
    pdcSecants[i] = dx > 0 ? (dy / dx) : 0;
  }

  // Initial tangents: flat neuromuscular shoulder at start, gentle tail at end
  pdcTangents[0] = pdcSecants[0] * 0.2;
  pdcTangents[7] = pdcSecants[6] * 0.3;
  for (let i = 1; i < 7; i++) {
    pdcTangents[i] = (pdcSecants[i - 1] + pdcSecants[i]) * 0.5;
  }

  // Fritsch-Carlson Monotonicity constraint: constrain tangents within ellipse alpha^2 + beta^2 <= 9
  for (let i = 0; i < 7; i++) {
    const s = pdcSecants[i];
    if (s === 0) {
      pdcTangents[i] = 0;
      pdcTangents[i + 1] = 0;
    } else {
      const alpha = pdcTangents[i] / s;
      const beta = pdcTangents[i + 1] / s;
      const magSq = alpha * alpha + beta * beta;
      if (magSq > 9.0) {
        const tau = 3.0 / Math.sqrt(magSq);
        pdcTangents[i] = tau * alpha * s;
        pdcTangents[i + 1] = tau * beta * s;
      }
    }
  }

  // 5. Evaluate Tracer Position & Instantaneous Power on Exact Monotone Spline (compounded 35% decelerated speed: 0.0003169)
  const tracerU = 0.5 + Math.sin(now * 0.0003169) * 0.44; // 0.06 to 0.94
  const tracerIdx = Math.min(6, Math.max(0, Math.floor(tracerU * 7)));
  const subT = (tracerU * 7) - tracerIdx;
  const x0 = pdcScreenCoords[tracerIdx * 2];
  const y0 = pdcScreenCoords[tracerIdx * 2 + 1];
  const x1 = pdcScreenCoords[(tracerIdx + 1) * 2];
  const y1 = pdcScreenCoords[(tracerIdx + 1) * 2 + 1];
  const segDx = x1 - x0;
  const cp1x = x0 + segDx / 3;
  const cp1y = y0 + (pdcTangents[tracerIdx] * segDx) / 3;
  const cp2x = x1 - segDx / 3;
  const cp2y = y1 - (pdcTangents[tracerIdx + 1] * segDx) / 3;

  // Exact Cubic Bézier evaluation for tracer node
  const invT = 1.0 - subT;
  const c0 = invT * invT * invT;
  const c1 = 3 * invT * invT * subT;
  const c2 = 3 * invT * subT * subT;
  const c3 = subT * subT * subT;
  const tx = c0 * x0 + c1 * cp1x + c2 * cp2x + c3 * x1;
  const ty = c0 * y0 + c1 * cp1y + c2 * cp2y + c3 * y1;

  // Power in Watts derived from tracer vertical height
  const tracerPwr = 150 + ((plotY + plotH - ty) / plotH) * 1050;

  // Synchronize telemetryState with tracer power so lower HUD and dashboard are in lockstep
  telemetryState[0] = tracerPwr;
  telemetryState[3] = tracerPwr / 69.5;
  telemetryState[4] = 18.0 + (tracerPwr / 1150) * 14.5;

  // Header: Left Label, Right Dynamic Wattage (consistent with tracer dot Y-value)
  const headerFontSize = Math.max(8, 9.5 * scale).toFixed(1);
  ctx.font = `700 ${headerFontSize}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace`;
  ctx.fillStyle = isHovered ? '#7dd3fc' : '#38bdf8';
  if (isHovered) {
    ctx.shadowColor = 'rgba(56, 189, 248, 0.70)';
    ctx.shadowBlur = 6;
  }
  ctx.fillText('PDC (WKO)', modX + Math.floor(6 * scale), modY + Math.floor(13 * scale));

  const displayPwr = Math.round(tracerPwr);
  ctx.fillStyle = isHovered ? '#ffffff' : '#f8fafc';
  if (isHovered) {
    ctx.shadowColor = 'rgba(255, 255, 255, 0.85)';
    ctx.shadowBlur = 8;
  }
  ctx.textAlign = 'right';
  ctx.fillText(`${displayPwr} W`, modX + modW - Math.floor(6 * scale), modY + Math.floor(13 * scale));
  ctx.textAlign = 'left';
  ctx.shadowColor = 'transparent';

  // Grid Lines & Ticks (1000W, 600W, 300W horizontal + 5s, 1m, 5m, 1h vertical calipers)
  ctx.fillStyle = isHovered ? '#f8fafc' : 'rgba(148, 163, 184, 0.70)';
  const tickFontSize = Math.max(6.5, 7.5 * scale).toFixed(1);
  ctx.font = `700 ${tickFontSize}px monospace`;

  const gridPowers = [1000, 600, 300];
  const gridYCoords = [0, 0, 0];
  for (let k = 0; k < gridPowers.length; k++) {
    const p = gridPowers[k];
    const ny = plotY + plotH - ((p - 150) / 1050) * plotH;
    gridYCoords[k] = ny;
    ctx.strokeStyle = isHovered ? 'rgba(56, 189, 248, 0.48)' : 'rgba(56, 189, 248, 0.22)';
    ctx.lineWidth = isHovered ? 1.2 : 1;
    ctx.beginPath();
    ctx.moveTo(plotX, ny);
    ctx.lineTo(plotX + plotW, ny);
    ctx.stroke();
    ctx.fillText(`${p}`, modX + Math.floor(3 * scale), ny + 2.5);
  }

  // Vertical Logarithmic Time Calipers (5s, 1m, 5m, 1h)
  const milestoneIndices = [1, 3, 4, 6];
  const milestoneXCoords = [0, 0, 0, 0];
  ctx.strokeStyle = isHovered ? 'rgba(56, 189, 248, 0.38)' : 'rgba(56, 189, 248, 0.16)';
  ctx.setLineDash([2, 3]);
  for (let m = 0; m < milestoneIndices.length; m++) {
    const idx = milestoneIndices[m];
    const px = pdcScreenCoords[idx * 2];
    milestoneXCoords[m] = px;
    ctx.beginPath();
    ctx.moveTo(px, plotY);
    ctx.lineTo(px, plotY + plotH);
    ctx.stroke();
  }
  ctx.setLineDash([]);

  // CAD Cross Ticks at Grid Intersections (Zero allocation)
  ctx.strokeStyle = isHovered ? 'rgba(56, 189, 248, 0.65)' : 'rgba(56, 189, 248, 0.40)';
  ctx.lineWidth = 1;
  const tickSize = Math.max(1.5, 2.0 * scale);
  for (let k = 0; k < gridYCoords.length; k++) {
    const gy = gridYCoords[k];
    for (let m = 0; m < milestoneXCoords.length; m++) {
      const gx = milestoneXCoords[m];
      ctx.beginPath();
      ctx.moveTo(gx - tickSize, gy); ctx.lineTo(gx + tickSize, gy);
      ctx.moveTo(gx, gy - tickSize); ctx.lineTo(gx, gy + tickSize);
      ctx.stroke();
    }
  }

  // =========================================================================
  // PDC Periodic Energization Engine: High-Voltage Sputter-On & Spark Fizzle
  // Period: 7300ms (Asynchronous prime-period oscillator)
  // =========================================================================
  const pdcPhaseMs = now % 7300;
  if (pdcPhaseMs < 1300) {
    // Select alternating wattage rung (600W threshold vs 1000W sprint)
    const targetIdx = Math.floor(now / 7300) % 2 === 0 ? 1 : 0;
    const targetY = gridYCoords[targetIdx];

    // Phase A: Sputter-On Ignition (0 - 180ms) - 3 staccato micro-arcs
    let surgeAlpha = 0;
    if (pdcPhaseMs < 180) {
      const stutter = Math.sin(pdcPhaseMs * 0.09) > 0 ? (0.45 + 0.55 * Math.sin(pdcPhaseMs * 0.16)) : 0.08;
      surgeAlpha = stutter * 0.95;
    } else {
      // Phase B & C: Peak Laser Sweep & Sputter-Off Quenching (180ms - 1300ms)
      const quenchDecay = Math.exp(-(pdcPhaseMs - 180) * 0.0036);
      const dropout = (pdcPhaseMs > 520 && pdcPhaseMs < 640) ? 0.20 : 1.0;
      surgeAlpha = quenchDecay * dropout * 0.88;
    }

    if (surgeAlpha > 0.02) {
      // Energized High-Voltage Grid Line Glow
      ctx.strokeStyle = `rgba(56, 189, 248, ${surgeAlpha.toFixed(3)})`;
      ctx.lineWidth = Math.max(1.4, 2.0 * scale);
      ctx.beginPath();
      ctx.moveTo(plotX, targetY);
      ctx.lineTo(plotX + plotW, targetY);
      ctx.stroke();

      // Core White Photonic Filament
      ctx.strokeStyle = `rgba(255, 255, 255, ${(surgeAlpha * 0.85).toFixed(3)})`;
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.moveTo(plotX, targetY);
      ctx.lineTo(plotX + plotW, targetY);
      ctx.stroke();

      // Traveling Laser Sweep Head (180ms - 650ms)
      if (pdcPhaseMs >= 180 && pdcPhaseMs < 750) {
        const sweepProgress = Math.min(1.0, (pdcPhaseMs - 180) / 470);
        const sweepX = plotX + sweepProgress * plotW;
        ctx.beginPath();
        ctx.arc(sweepX, targetY, Math.max(2.5, 3.8 * scale), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${surgeAlpha.toFixed(3)})`;
        ctx.fill();

        // 4 Micro-Sparks scattering along the sweep with zero allocation
        for (let s = 0; s < 4; s++) {
          const sparkOffset = ((pdcPhaseMs * (s + 1) * 7) % 19) - 9;
          const sparkY = targetY + (Math.sin(pdcPhaseMs * 0.04 + s * 1.5) * 3.5 * scale);
          const sparkX = Math.max(plotX, Math.min(plotX + plotW, sweepX + sparkOffset));
          ctx.beginPath();
          ctx.arc(sparkX, sparkY, 1.2 * scale, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(56, 189, 248, ${(surgeAlpha * 0.9).toFixed(3)})`;
          ctx.fill();
        }
      }
    }
  }

  // Draw Area Gradient Fill (lockstep alpha and luminescence sync with instantaneous power surge + hover boost)
  const alphaBoost = Math.max(-0.08, Math.min(0.08, (pwrDelta / 18) * 0.08));
  const hBoost = isHovered ? 0.20 : 0;
  const grad = ctx.createLinearGradient(plotX, pdcScreenCoords[1], plotX + plotW, plotY + plotH);
  grad.addColorStop(0, `rgba(239, 68, 68, ${(0.42 + alphaBoost + hBoost).toFixed(3)})`);   // Sprint Red
  grad.addColorStop(0.24, `rgba(217, 70, 239, ${(0.32 + alphaBoost * 0.85 + hBoost).toFixed(3)})`); // FRC Purple
  grad.addColorStop(0.55, `rgba(56, 189, 248, ${(0.25 + alphaBoost * 0.70 + hBoost * 0.8).toFixed(3)})`); // VO2 Cyan
  grad.addColorStop(0.82, `rgba(16, 185, 129, ${(0.20 + alphaBoost * 0.60 + hBoost * 0.7).toFixed(3)})`); // FTP Green
  grad.addColorStop(1.0, `rgba(100, 116, 139, ${(0.12 + alphaBoost * 0.50 + hBoost * 0.5).toFixed(3)})`); // Tempo Slate

  ctx.beginPath();
  ctx.moveTo(pdcScreenCoords[0], pdcScreenCoords[1]);
  for (let i = 0; i < 7; i++) {
    const x0_i = pdcScreenCoords[i * 2];
    const y0_i = pdcScreenCoords[i * 2 + 1];
    const x1_i = pdcScreenCoords[(i + 1) * 2];
    const y1_i = pdcScreenCoords[(i + 1) * 2 + 1];
    const dx_i = x1_i - x0_i;
    const cp1x_i = x0_i + dx_i / 3;
    const cp1y_i = y0_i + (pdcTangents[i] * dx_i) / 3;
    const cp2x_i = x1_i - dx_i / 3;
    const cp2y_i = y1_i - (pdcTangents[i + 1] * dx_i) / 3;
    ctx.bezierCurveTo(cp1x_i, cp1y_i, cp2x_i, cp2y_i, x1_i, y1_i);
  }
  ctx.lineTo(pdcScreenCoords[14], pdcScreenCoords[15]);
  ctx.lineTo(plotX + plotW, plotY + plotH);
  ctx.lineTo(plotX, plotY + plotH);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();

  // Top Spline Laser Stroke (Dual-Pass on hover for photonic laser bloom)
  ctx.beginPath();
  ctx.moveTo(pdcScreenCoords[0], pdcScreenCoords[1]);
  for (let i = 0; i < 7; i++) {
    const x0_i = pdcScreenCoords[i * 2];
    const y0_i = pdcScreenCoords[i * 2 + 1];
    const x1_i = pdcScreenCoords[(i + 1) * 2];
    const y1_i = pdcScreenCoords[(i + 1) * 2 + 1];
    const dx_i = x1_i - x0_i;
    const cp1x_i = x0_i + dx_i / 3;
    const cp1y_i = y0_i + (pdcTangents[i] * dx_i) / 3;
    const cp2x_i = x1_i - dx_i / 3;
    const cp2y_i = y1_i - (pdcTangents[i + 1] * dx_i) / 3;
    ctx.bezierCurveTo(cp1x_i, cp1y_i, cp2x_i, cp2y_i, x1_i, y1_i);
  }
  ctx.lineTo(pdcScreenCoords[14], pdcScreenCoords[15]);
  ctx.strokeStyle = isHovered ? 'rgba(56, 189, 248, 0.98)' : 'rgba(56, 189, 248, 0.90)';
  ctx.lineWidth = Math.max(1.2, (isHovered ? 2.4 : 1.6) * scale);
  ctx.stroke();

  if (isHovered) {
    // Sharp white laser core
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = Math.max(0.9, 1.1 * scale);
    ctx.stroke();
  }

  // Time Axis Milestones (5s, 1m, 5m, 1h)
  ctx.fillStyle = isHovered ? '#f8fafc' : 'rgba(148, 163, 184, 0.75)';
  ctx.font = `700 ${tickFontSize}px monospace`;
  for (let m = 0; m < milestoneIndices.length; m++) {
    const idx = milestoneIndices[m];
    const px = pdcScreenCoords[idx * 2];
    ctx.fillText(PDC_PTS[idx].lbl, px - Math.floor(5 * scale), plotY + plotH + Math.floor(11 * scale));
  }

  // Tracer dashed drop line
  ctx.beginPath();
  ctx.setLineDash([2, 2]);
  ctx.strokeStyle = 'rgba(56, 189, 248, 0.45)';
  ctx.lineWidth = 1;
  ctx.moveTo(tx, ty);
  ctx.lineTo(tx, plotY + plotH);
  ctx.stroke();
  ctx.setLineDash([]);

  // Tracer Bead
  ctx.beginPath();
  ctx.arc(tx, ty, Math.max(2.2, 3.2 * scale) + Math.sin(now * 0.006) * 1.0, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.strokeStyle = 'rgba(56, 189, 248, 0.95)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.restore();
}

/**
 * Module 2: VO2 Max Aerobic Capacity Ribbon
 * Multi-month fitness trajectory with dynamic undulating waves, fluctuating confidence bandwidth,
 * vibrant 4-stop cyan-to-gold energy gradient, and radiant breathing peak node (+30% taller elevation).
 */
function drawVo2MaxModule(bounds, now, alpha = panelHoverAlphas[1]) {
  const { modX, modY, modW, modH, scale = 1.0 } = computeVo2Bounds(bounds);
  const isHovered = alpha >= 0.85;

  ctx.save();
  // Soft ambient drop shadow with theme-colored amber bloom on hover
  ctx.shadowColor = isHovered ? 'rgba(251, 191, 36, 0.40)' : 'rgba(0, 0, 0, 0.60)';
  ctx.shadowBlur = Math.floor((isHovered ? 20 : 8) * scale);
  ctx.shadowOffsetY = Math.floor((isHovered ? 3 : 2) * scale);

  // High-Luminance Illuminated Cockpit Backlighting & Surface
  if (isHovered) {
    const vo2Grad = ctx.createLinearGradient(modX, modY, modX, modY + modH);
    vo2Grad.addColorStop(0, '#1e293b'); // slate-800 illuminated top
    vo2Grad.addColorStop(0.45, '#172236');
    vo2Grad.addColorStop(1, '#0b1120'); // deep contrast bottom
    ctx.fillStyle = vo2Grad;
  } else {
    ctx.fillStyle = (alpha >= 0.98) ? '#0f172a' : `rgba(15, 23, 42, ${alpha.toFixed(3)})`;
  }
  ctx.strokeStyle = isHovered ? 'rgba(251, 191, 36, 0.95)' : 'rgba(56, 189, 248, 0.28)';
  ctx.lineWidth = isHovered ? 1.6 : 1.0;
  ctx.strokeRect(modX, modY, modW, modH);
  ctx.fillRect(modX, modY, modW, modH);

  // Reset shadow for crisp hairline strokes
  ctx.shadowColor = 'transparent';

  if (isHovered) {
    // Ambient inner radial warm amber backlight
    const vo2InnerGlow = ctx.createRadialGradient(
      modX + modW * 0.5, modY + modH * 0.45, 10,
      modX + modW * 0.5, modY + modH * 0.45, Math.max(modW, modH) * 0.65
    );
    vo2InnerGlow.addColorStop(0, 'rgba(251, 191, 36, 0.14)');
    vo2InnerGlow.addColorStop(0.65, 'rgba(20, 184, 166, 0.04)');
    vo2InnerGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = vo2InnerGlow;
    ctx.fillRect(modX, modY, modW, modH);
  }

  // Bespoke High-Tech Framing 2: Biometric Caliper Brackets & Holographic Shimmer
  const caliperL = Math.max(6, Math.floor(12 * scale));

  // Traveling Holographic Amber/Teal Glint Shimmer (replaces cheap moving dotted line)
  const vo2GlintT = ((now + 1800) * 0.00031) % 1.0;
  const vo2GlintSpan = Math.max(45, Math.floor(70 * scale));
  const vo2GlintX = modX + vo2GlintT * (modW + vo2GlintSpan) - vo2GlintSpan * 0.5;
  const vo2GlintGrad = ctx.createLinearGradient(vo2GlintX - vo2GlintSpan * 0.5, modY, vo2GlintX + vo2GlintSpan * 0.5, modY);
  vo2GlintGrad.addColorStop(0, 'rgba(20, 184, 166, 0)');
  vo2GlintGrad.addColorStop(0.3, 'rgba(251, 191, 36, 0.55)');
  vo2GlintGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.95)');
  vo2GlintGrad.addColorStop(0.7, 'rgba(251, 191, 36, 0.65)');
  vo2GlintGrad.addColorStop(1, 'rgba(20, 184, 166, 0)');

  // Multi-harmonic phosphor shimmer
  const bioPulse = (now * 0.0013) % (Math.PI * 2);
  const bioAlpha = 0.52 + 0.32 * Math.sin(bioPulse) * Math.cos(now * 0.00065);
  ctx.strokeStyle = isHovered ? vo2GlintGrad : `rgba(251, 191, 36, ${(bioAlpha * 0.65).toFixed(3)})`;
  ctx.lineWidth = isHovered ? 1.6 : 1.2;
  ctx.strokeRect(modX, modY, modW, modH);

  if (isHovered) {
    ctx.strokeStyle = vo2GlintGrad;
    ctx.lineWidth = 1.8;
    ctx.strokeRect(modX, modY, modW, modH);
  }

  // Luminous dual-tone biometric calipers with high-contrast corner tick marks
  ctx.strokeStyle = isHovered ? 'rgba(251, 191, 36, 0.98)' : 'rgba(251, 191, 36, 0.75)';
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(modX + caliperL, modY); ctx.lineTo(modX, modY); ctx.lineTo(modX, modY + caliperL);
  ctx.moveTo(modX + modW - caliperL, modY); ctx.lineTo(modX + modW, modY); ctx.lineTo(modX + modW, modY + caliperL);
  ctx.stroke();

  ctx.strokeStyle = isHovered ? 'rgba(20, 184, 166, 0.95)' : 'rgba(20, 184, 166, 0.70)';
  ctx.beginPath();
  ctx.moveTo(modX, modY + modH - caliperL); ctx.lineTo(modX, modY + modH); ctx.lineTo(modX + caliperL, modY + modH);
  ctx.moveTo(modX + modW - caliperL, modY + modH); ctx.lineTo(modX + modW, modY + modH); ctx.lineTo(modX + modW, modY + modH - caliperL);
  ctx.stroke();

  // Header: Left Label, Right Dynamic Peak Readout
  const headerFontSize = Math.max(8, 9.5 * scale).toFixed(1);
  ctx.font = `700 ${headerFontSize}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace`;
  ctx.fillStyle = isHovered ? '#7dd3fc' : '#38bdf8';
  if (isHovered) {
    ctx.shadowColor = 'rgba(56, 189, 248, 0.70)';
    ctx.shadowBlur = 6;
  }
  ctx.fillText('VO2 MAX', modX + Math.floor(6 * scale), modY + Math.floor(13 * scale));

  // Dynamic peak value with physiological respiration rhythm (slowed by 35%: 0.0032 * 0.65 = 0.00208)
  const livePeak = (86.4 + Math.sin(now * 0.00208) * 0.2).toFixed(1);
  ctx.fillStyle = '#fbbf24';
  if (isHovered) {
    ctx.shadowColor = 'rgba(251, 191, 36, 0.85)';
    ctx.shadowBlur = 8;
  }
  ctx.textAlign = 'right';
  ctx.fillText(`${livePeak} ML/KG/MIN`, modX + modW - Math.floor(6 * scale), modY + Math.floor(13 * scale));
  ctx.textAlign = 'left';
  ctx.shadowColor = 'transparent';

  // Plot Area (expanded vertical clearance for wide undulating ribbon waves)
  const padL = Math.floor(26 * scale);
  const padR = Math.floor(8 * scale);
  const padT = Math.floor(20 * scale);
  const padB = Math.floor(16 * scale);
  const plotX = modX + padL;
  const plotY = modY + padT;
  const plotW = Math.max(20, modW - padL - padR);
  const plotH = Math.max(20, modH - padT - padB);

  const vMin = 74.0;
  const vMax = 90.0;
  const vSpan = vMax - vMin;

  // 1. Aerobic Level Grid Rungs (76, 80, 84, 88 ml/kg/min horizontal)
  const vo2GridLevels = [88, 84, 80, 76];
  const vo2GridYCoords = [0, 0, 0, 0];
  ctx.fillStyle = isHovered ? '#f8fafc' : 'rgba(148, 163, 184, 0.70)';
  const tickFontSize = Math.max(6.5, 7.5 * scale).toFixed(1);
  ctx.font = `700 ${tickFontSize}px monospace`;

  for (let k = 0; k < vo2GridLevels.length; k++) {
    const lvl = vo2GridLevels[k];
    const gy = plotY + plotH - ((lvl - vMin) / vSpan) * plotH;
    vo2GridYCoords[k] = gy;
    ctx.strokeStyle = lvl >= 88 ? (isHovered ? 'rgba(251, 191, 36, 0.52)' : 'rgba(251, 191, 36, 0.24)') : (isHovered ? 'rgba(56, 189, 248, 0.45)' : 'rgba(56, 189, 248, 0.20)');
    ctx.lineWidth = isHovered ? 1.2 : 1;
    ctx.beginPath();
    ctx.moveTo(plotX, gy);
    ctx.lineTo(plotX + plotW, gy);
    ctx.stroke();
    ctx.fillText(`${lvl}`, modX + Math.floor(4 * scale), gy + 2.5);
  }

  // Vertical Monthly Calipers (Jan - Jun)
  const vo2MonthXCoords = [0, 0, 0, 0, 0, 0];
  ctx.strokeStyle = isHovered ? 'rgba(56, 189, 248, 0.35)' : 'rgba(56, 189, 248, 0.14)';
  ctx.setLineDash([2, 3]);
  for (let i = 0; i < 6; i++) {
    const mx = plotX + (i / 5) * plotW;
    vo2MonthXCoords[i] = mx;
    ctx.beginPath();
    ctx.moveTo(mx, plotY);
    ctx.lineTo(mx, plotY + plotH);
    ctx.stroke();
  }
  ctx.setLineDash([]);

  // CAD Cross Ticks at Grid Intersections
  ctx.strokeStyle = isHovered ? 'rgba(56, 189, 248, 0.65)' : 'rgba(56, 189, 248, 0.38)';
  ctx.lineWidth = 1;
  const vo2TickSize = Math.max(1.5, 2.0 * scale);
  for (let k = 0; k < vo2GridYCoords.length; k++) {
    const gy = vo2GridYCoords[k];
    for (let i = 0; i < 6; i++) {
      const gx = vo2MonthXCoords[i];
      ctx.beginPath();
      ctx.moveTo(gx - vo2TickSize, gy); ctx.lineTo(gx + vo2TickSize, gy);
      ctx.moveTo(gx, gy - vo2TickSize); ctx.lineTo(gx, gy + vo2TickSize);
      ctx.stroke();
    }
  }

  // =========================================================================
  // VO2 Max Periodic Energization Engine: Cellular Background Sputter & Sonar
  // Period: 10700ms (Asynchronous prime-period oscillator, phase offset 2400ms)
  // =========================================================================
  const vo2PhaseMs = (now + 2400) % 10700;
  if (vo2PhaseMs < 2200) {
    // Phase A: Background Cell Sputter-On (0 - 450ms) - Staccato cellular wash
    let cellSputterAlpha = 0;
    if (vo2PhaseMs < 450) {
      const stutter = Math.sin(vo2PhaseMs * 0.08) > 0 ? (0.35 + 0.65 * Math.sin(vo2PhaseMs * 0.15)) : 0.05;
      cellSputterAlpha = stutter * 0.16;
    } else {
      // Phase B: Sputter-Off Mist Dissolution (450ms - 2200ms)
      const quenchDecay = Math.exp(-(vo2PhaseMs - 450) * 0.0022);
      const dropout = (vo2PhaseMs > 900 && vo2PhaseMs < 1050) ? 0.30 : 1.0;
      cellSputterAlpha = quenchDecay * dropout * 0.14;
    }

    if (cellSputterAlpha > 0.005) {
      // Energize Upper-Right Aerobic Quadrant Cell
      const cellX = plotX + 0.5 * plotW;
      const cellY = plotY;
      const cellW = 0.5 * plotW;
      const cellH = vo2GridYCoords[2] - plotY;
      ctx.fillStyle = `rgba(251, 191, 36, ${cellSputterAlpha.toFixed(3)})`;
      ctx.fillRect(cellX, cellY, cellW, cellH);

      // Sputter Border Highlight
      ctx.strokeStyle = `rgba(251, 191, 36, ${(cellSputterAlpha * 3.5).toFixed(3)})`;
      ctx.lineWidth = 1;
      ctx.strokeRect(cellX, cellY, cellW, cellH);
    }

    // Expanding Respiration Sonar Wavefront (100ms - 1900ms)
    if (vo2PhaseMs >= 100 && vo2PhaseMs < 1900) {
      const sonarProgress = (vo2PhaseMs - 100) / 1800;
      const sonarRadius = sonarProgress * (plotW * 0.95);
      const sonarAlpha = Math.sin(sonarProgress * Math.PI) * 0.70;

      const tipAnchorX = plotX + plotW;
      const tipAnchorY = vo2GridYCoords[0]; // Near 88 ml/kg/min peak

      ctx.save();
      ctx.beginPath();
      ctx.arc(tipAnchorX, tipAnchorY, sonarRadius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(251, 191, 36, ${sonarAlpha.toFixed(3)})`;
      ctx.lineWidth = Math.max(1.2, 1.8 * scale);
      ctx.stroke();

      // Soft secondary teal echo
      if (sonarRadius > 14) {
        ctx.beginPath();
        ctx.arc(tipAnchorX, tipAnchorY, sonarRadius - 12, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(20, 184, 166, ${(sonarAlpha * 0.5).toFixed(3)})`;
        ctx.lineWidth = 1.0;
        ctx.stroke();
      }
      ctx.restore();
    }
  }

  // Multi-Stop Cyan-to-Gold Energy Gradient across ribbon (boosted on hover)
  const grad = ctx.createLinearGradient(plotX, plotY, plotX + plotW, plotY);
  if (isHovered) {
    grad.addColorStop(0.00, 'rgba(14, 165, 233, 0.65)'); // Deep Electric Cyan (Base Foundation)
    grad.addColorStop(0.35, 'rgba(20, 184, 166, 0.60)'); // Vibrant Emerald Teal (Aerobic Build)
    grad.addColorStop(0.72, 'rgba(245, 158, 11, 0.62)'); // Warm Amber Gold (Threshold Surge)
    grad.addColorStop(1.00, 'rgba(251, 191, 36, 0.75)'); // Radiant Champagne Gold (Peak VO2 Max)
  } else {
    grad.addColorStop(0.00, 'rgba(14, 165, 233, 0.45)'); // Deep Electric Cyan (Base Foundation)
    grad.addColorStop(0.35, 'rgba(20, 184, 166, 0.42)'); // Vibrant Emerald Teal (Aerobic Build)
    grad.addColorStop(0.72, 'rgba(245, 158, 11, 0.45)'); // Warm Amber Gold (Threshold Surge)
    grad.addColorStop(1.00, 'rgba(251, 191, 36, 0.55)'); // Radiant Champagne Gold (Peak VO2 Max)
  }

  // Dynamic Wave Animation & Organic Band Width Fluctuation (-35% speed, -30% band width)
  // Compute screen coordinates with traveling respiration wave and varying band width
  for (let i = 0; i < 6; i++) {
    const baseMed = VO2_MED[i];
    // Traveling sinusoidal wave propagating from Jan to Jun (decelerated by 35%: 0.0022 * 0.65 = 0.00143)
    const wavePhase = (now * 0.00143) - (i * 0.75);
    const ripple = Math.sin(wavePhase) * 1.1 + Math.cos(now * 0.00247 + i * 1.3) * 0.6;
    const currentMed = baseMed + ripple;

    // Dynamic band width variation: expands during peak respiration and contracts during recovery
    const breathFactor = 1.0 + Math.sin(now * 0.00182 + i * 0.9) * 0.42 + Math.cos(now * 0.00104 - i * 0.5) * 0.23;
    const halfBand = 1.68 * breathFactor; // Reduced by 30% (2.4 * 0.70 = 1.68)

    const px = plotX + (i / 5) * plotW;
    const pyUp = plotY + plotH - (((currentMed + halfBand) - vMin) / vSpan) * plotH;
    const pyDn = plotY + plotH - (((currentMed - halfBand) - vMin) / vSpan) * plotH;
    const pyMed = plotY + plotH - ((currentMed - vMin) / vSpan) * plotH;

    vo2UpCoords[i * 2] = px;
    vo2UpCoords[i * 2 + 1] = pyUp;
    vo2DnCoords[i * 2] = px;
    vo2DnCoords[i * 2 + 1] = pyDn;
    vo2MedCoords[i * 2] = px;
    vo2MedCoords[i * 2 + 1] = pyMed;
  }

  // Draw Confidence Interval Ribbon with smooth quadratic splines and multi-stop gradient
  ctx.beginPath();
  ctx.moveTo(vo2UpCoords[0], vo2UpCoords[1]);
  for (let i = 0; i < 5; i++) {
    const x0 = vo2UpCoords[i * 2];
    const y0 = vo2UpCoords[i * 2 + 1];
    const x1 = vo2UpCoords[(i + 1) * 2];
    const y1 = vo2UpCoords[(i + 1) * 2 + 1];
    const mx = (x0 + x1) * 0.5;
    const my = (y0 + y1) * 0.5;
    ctx.quadraticCurveTo(x0, y0, mx, my);
  }
  ctx.lineTo(vo2UpCoords[10], vo2UpCoords[11]);
  ctx.lineTo(vo2DnCoords[10], vo2DnCoords[11]);
  for (let i = 4; i >= 0; i--) {
    const x0 = vo2DnCoords[(i + 1) * 2];
    const y0 = vo2DnCoords[(i + 1) * 2 + 1];
    const x1 = vo2DnCoords[i * 2];
    const y1 = vo2DnCoords[i * 2 + 1];
    const mx = (x0 + x1) * 0.5;
    const my = (y0 + y1) * 0.5;
    ctx.quadraticCurveTo(x0, y0, mx, my);
  }
  ctx.lineTo(vo2DnCoords[0], vo2DnCoords[1]);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();

  // Upper Boundary Luminous Spline
  ctx.beginPath();
  ctx.moveTo(vo2UpCoords[0], vo2UpCoords[1]);
  for (let i = 0; i < 5; i++) {
    const mx = (vo2UpCoords[i * 2] + vo2UpCoords[(i + 1) * 2]) * 0.5;
    const my = (vo2UpCoords[i * 2 + 1] + vo2UpCoords[(i + 1) * 2 + 1]) * 0.5;
    ctx.quadraticCurveTo(vo2UpCoords[i * 2], vo2UpCoords[i * 2 + 1], mx, my);
  }
  ctx.lineTo(vo2UpCoords[10], vo2UpCoords[11]);
  ctx.strokeStyle = isHovered ? 'rgba(56, 189, 248, 0.95)' : 'rgba(56, 189, 248, 0.75)';
  ctx.lineWidth = Math.max(1.0, (isHovered ? 1.6 : 1.2) * scale);
  ctx.stroke();

  // Lower Boundary Anchor Spline
  ctx.beginPath();
  ctx.moveTo(vo2DnCoords[0], vo2DnCoords[1]);
  for (let i = 0; i < 5; i++) {
    const mx = (vo2DnCoords[i * 2] + vo2DnCoords[(i + 1) * 2]) * 0.5;
    const my = (vo2DnCoords[i * 2 + 1] + vo2DnCoords[(i + 1) * 2 + 1]) * 0.5;
    ctx.quadraticCurveTo(vo2DnCoords[i * 2], vo2DnCoords[i * 2 + 1], mx, my);
  }
  ctx.lineTo(vo2DnCoords[10], vo2DnCoords[11]);
  ctx.strokeStyle = isHovered ? 'rgba(245, 158, 11, 0.85)' : 'rgba(245, 158, 11, 0.60)';
  ctx.lineWidth = Math.max(0.8, (isHovered ? 1.3 : 1.0) * scale);
  ctx.stroke();

  // Median Trajectory Spline (energized laser core)
  ctx.beginPath();
  ctx.moveTo(vo2MedCoords[0], vo2MedCoords[1]);
  for (let i = 0; i < 5; i++) {
    const mx = (vo2MedCoords[i * 2] + vo2MedCoords[(i + 1) * 2]) * 0.5;
    const my = (vo2MedCoords[i * 2 + 1] + vo2MedCoords[(i + 1) * 2 + 1]) * 0.5;
    ctx.quadraticCurveTo(vo2MedCoords[i * 2], vo2MedCoords[i * 2 + 1], mx, my);
  }
  ctx.lineTo(vo2MedCoords[10], vo2MedCoords[11]);
  ctx.strokeStyle = isHovered ? 'rgba(254, 240, 138, 0.98)' : 'rgba(254, 240, 138, 0.95)';
  ctx.lineWidth = Math.max(1.2, (isHovered ? 2.5 : 1.8) * scale);
  ctx.stroke();

  if (isHovered) {
    // Sharp white core filament on hover
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = Math.max(0.9, 1.1 * scale);
    ctx.stroke();
  }

  // Baseline Reference Ticks
  ctx.fillStyle = isHovered ? '#f8fafc' : 'rgba(148, 163, 184, 0.75)';
  ctx.font = `700 ${tickFontSize}px monospace`;
  ctx.fillText('BASE 78.2', plotX + 2, plotY + plotH + Math.floor(11 * scale));
  ctx.textAlign = 'right';
  ctx.fillText('PEAK 86.4', plotX + plotW, plotY + plotH + Math.floor(11 * scale));
  ctx.textAlign = 'left';

  // Radiant Breathing Current Peak Node at tip (June)
  const tipX = vo2MedCoords[10];
  const tipY = vo2MedCoords[11];
  const breath = Math.sin(now * 0.00228) * (isHovered ? 2.0 : 1.5);

  // Outer radiant amber bloom
  ctx.beginPath();
  ctx.arc(tipX, tipY, Math.max(4.0, (isHovered ? 8.5 : 7.0) * scale) + breath, 0, Math.PI * 2);
  ctx.fillStyle = isHovered ? 'rgba(251, 191, 36, 0.45)' : 'rgba(251, 191, 36, 0.32)';
  ctx.fill();

  // Mid champagne halo
  ctx.beginPath();
  ctx.arc(tipX, tipY, Math.max(2.8, (isHovered ? 5.2 : 4.5) * scale) + breath * 0.5, 0, Math.PI * 2);
  ctx.fillStyle = '#fbbf24';
  ctx.fill();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.4;
  ctx.stroke();

  // Specular white center
  ctx.beginPath();
  ctx.arc(tipX, tipY, Math.max(1.2, 1.8 * scale), 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();

  ctx.restore();
}

/**
 * Module 3: Heart Rate vs. Power Scatter Matrix
 * 48-point multi-zone scatter constellation with active linear regression vector
 */
function drawHrPwrScatterModule(bounds, now, alpha = panelHoverAlphas[2]) {
  const { modX, modY, modW, modH, scale = 1.0 } = computeHrPwrBounds(bounds);
  const isHovered = alpha >= 0.85;

  ctx.save();
  // Soft ambient drop shadow with theme-colored rose bloom on hover
  ctx.shadowColor = isHovered ? 'rgba(244, 63, 94, 0.40)' : 'rgba(0, 0, 0, 0.60)';
  ctx.shadowBlur = Math.floor((isHovered ? 20 : 8) * scale);
  ctx.shadowOffsetY = Math.floor((isHovered ? 3 : 2) * scale);

  // High-Luminance Illuminated Cockpit Backlighting & Surface
  if (isHovered) {
    const hrGrad = ctx.createLinearGradient(modX, modY, modX, modY + modH);
    hrGrad.addColorStop(0, '#1e293b'); // slate-800 illuminated top
    hrGrad.addColorStop(0.45, '#191e30');
    hrGrad.addColorStop(1, '#0b1120'); // deep contrast bottom
    ctx.fillStyle = hrGrad;
  } else {
    ctx.fillStyle = (alpha >= 0.98) ? '#0f172a' : `rgba(15, 23, 42, ${alpha.toFixed(3)})`;
  }
  ctx.strokeStyle = isHovered ? 'rgba(244, 63, 94, 0.95)' : 'rgba(56, 189, 248, 0.28)';
  ctx.lineWidth = isHovered ? 1.6 : 1.0;
  ctx.strokeRect(modX, modY, modW, modH);
  ctx.fillRect(modX, modY, modW, modH);

  // Reset shadow for crisp hairline strokes
  ctx.shadowColor = 'transparent';

  if (isHovered) {
    // Ambient inner radial rose backlight
    const hrInnerGlow = ctx.createRadialGradient(
      modX + modW * 0.5, modY + modH * 0.45, 10,
      modX + modW * 0.5, modY + modH * 0.45, Math.max(modW, modH) * 0.65
    );
    hrInnerGlow.addColorStop(0, 'rgba(244, 63, 94, 0.14)');
    hrInnerGlow.addColorStop(0.65, 'rgba(244, 63, 94, 0.04)');
    hrInnerGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = hrInnerGlow;
    ctx.fillRect(modX, modY, modW, modH);
  }

  // Bespoke High-Tech Framing 3: Cybernetic Bezel & Cardiovascular Systolic Pulse (162 BPM)
  const hrPulsePhase = (now % 370) / 370;
  const pulseAlpha = Math.pow(Math.max(0, 1.0 - hrPulsePhase), 3.0);
  ctx.fillStyle = `rgba(244, 63, 94, ${(0.35 + pulseAlpha * 0.55).toFixed(3)})`;
  ctx.fillRect(modX, modY, Math.max(1, Math.round(1.5 * scale)), modH);

  ctx.strokeStyle = isHovered ? 'rgba(244, 63, 94, 0.85)' : `rgba(244, 63, 94, ${(0.22 + pulseAlpha * 0.45).toFixed(3)})`;
  ctx.lineWidth = 1 + (pulseAlpha > 0.6 ? 1 : 0);
  ctx.strokeRect(modX, modY, modW, modH);

  // Cybernetic corner tick marks in rose
  const tickS = Math.max(4, Math.floor(7 * scale));
  ctx.strokeStyle = isHovered ? 'rgba(244, 63, 94, 0.95)' : 'rgba(244, 63, 94, 0.75)';
  ctx.beginPath();
  ctx.moveTo(modX + modW - tickS, modY); ctx.lineTo(modX + modW, modY); ctx.lineTo(modX + modW, modY + tickS);
  ctx.moveTo(modX + modW - tickS, modY + modH); ctx.lineTo(modX + modW, modY + modH); ctx.lineTo(modX + modW, modY + modH - tickS);
  ctx.stroke();

  // Header: Left Label, Right Regression & EF Metrics
  const headerFontSize = Math.max(8, 9.5 * scale).toFixed(1);
  ctx.font = `700 ${headerFontSize}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace`;
  ctx.fillStyle = isHovered ? '#7dd3fc' : '#38bdf8';
  if (isHovered) {
    ctx.shadowColor = 'rgba(56, 189, 248, 0.70)';
    ctx.shadowBlur = 6;
  }
  ctx.fillText('HR VS PWR', modX + Math.floor(6 * scale), modY + Math.floor(13 * scale));

  ctx.fillStyle = '#fbbf24';
  if (isHovered) {
    ctx.shadowColor = 'rgba(251, 191, 36, 0.85)';
    ctx.shadowBlur = 8;
  }
  ctx.textAlign = 'right';
  ctx.fillText('r² 0.94  EF 1.80', modX + modW - Math.floor(6 * scale), modY + Math.floor(13 * scale));
  ctx.textAlign = 'left';
  ctx.shadowColor = 'transparent';

  // Plot Area
  const padL = Math.floor(26 * scale);
  const padR = Math.floor(8 * scale);
  const padT = Math.floor(20 * scale);
  const padB = Math.floor(16 * scale);
  const plotX = modX + padL;
  const plotY = modY + padT;
  const plotW = Math.max(20, modW - padL - padR);
  const plotH = Math.max(20, modH - padT - padB);

  // Grid Lines & Ticks (120, 140, 160, 180 BPM horizontal + 150W, 250W, 350W vertical)
  const hrGridLevels = [180, 160, 140, 120];
  const hrGridYCoords = [0, 0, 0, 0];
  ctx.fillStyle = isHovered ? '#f8fafc' : 'rgba(148, 163, 184, 0.70)';
  const tickFontSize = Math.max(6.5, 7.5 * scale).toFixed(1);
  ctx.font = `700 ${tickFontSize}px monospace`;

  for (let k = 0; k < hrGridLevels.length; k++) {
    const hrVal = hrGridLevels[k];
    const gy = plotY + plotH - ((hrVal - 100) / 90) * plotH;
    hrGridYCoords[k] = gy;
    ctx.strokeStyle = isHovered ? 'rgba(56, 189, 248, 0.48)' : 'rgba(56, 189, 248, 0.22)';
    ctx.lineWidth = isHovered ? 1.2 : 1;
    ctx.beginPath();
    ctx.moveTo(plotX, gy);
    ctx.lineTo(plotX + plotW, gy);
    ctx.stroke();
    ctx.fillText(`${hrVal}`, modX + Math.floor(4 * scale), gy + 2.5);
  }

  // Vertical Wattage Columns (150W, 250W, 350W)
  const hrPwrCols = [150, 250, 350];
  const hrPwrXCoords = [0, 0, 0];
  ctx.strokeStyle = isHovered ? 'rgba(56, 189, 248, 0.38)' : 'rgba(56, 189, 248, 0.16)';
  ctx.setLineDash([2, 3]);
  for (let c = 0; c < hrPwrCols.length; c++) {
    const pw = hrPwrCols[c];
    const gx = plotX + ((pw - 100) / 300) * plotW;
    hrPwrXCoords[c] = gx;
    ctx.beginPath();
    ctx.moveTo(gx, plotY);
    ctx.lineTo(gx, plotY + plotH);
    ctx.stroke();
    if (c === 0) ctx.fillText('150W', gx - Math.floor(10 * scale), plotY + plotH + Math.floor(11 * scale));
    else if (c === 2) ctx.fillText('350W', gx - Math.floor(14 * scale), plotY + plotH + Math.floor(11 * scale));
  }
  ctx.setLineDash([]);

  // CAD Cross Ticks at Cartesian Intersections
  ctx.strokeStyle = 'rgba(56, 189, 248, 0.40)';
  ctx.lineWidth = 1;
  const hrTickSize = Math.max(1.5, 2.0 * scale);
  for (let k = 0; k < hrGridYCoords.length; k++) {
    const gy = hrGridYCoords[k];
    for (let c = 0; c < hrPwrXCoords.length; c++) {
      const gx = hrPwrXCoords[c];
      ctx.beginPath();
      ctx.moveTo(gx - hrTickSize, gy); ctx.lineTo(gx + hrTickSize, gy);
      ctx.moveTo(gx, gy - hrTickSize); ctx.lineTo(gx, gy + hrTickSize);
      ctx.stroke();
    }
  }

  // Active Telemetry Point Pulse: 35% decelerated motion with smooth non-linear acceleration/deceleration,
  // reduced X-range fluctuation (212W - 308W), and balanced orthogonal wandering above, on, and below the regression line
  const motionPhase = now * 0.00072 + Math.sin(now * 0.00036) * 0.50; // Dynamic velocity modulation & acceleration easing
  const scatterPwr = 260 + Math.sin(motionPhase) * 38 + Math.cos(motionPhase * 0.45) * 10; // Reduced X-range: 212W to 308W
  const lineHr = 78.64 + 0.272 * scatterPwr; // Exact base height on the dashed regression line
  const wanderOffset = Math.sin(now * 0.00065) * 4.8 + Math.cos(now * 0.00130 + 1.2) * 2.2; // Wanders above (+), on (0), and below (-)
  const scatterHr = lineHr + wanderOffset;

  const curX = plotX + ((scatterPwr - 100) / 300) * plotW;
  const curY = plotY + plotH - ((scatterHr - 100) / 90) * plotH;

  // =========================================================================
  // HR vs PWR Periodic Energization Engine: 3-Mode Dynamic Crosshair Dispatcher
  // Period: 5300ms (Asynchronous prime-period oscillator, phase offset 4900ms)
  // =========================================================================
  const hrPhaseMs = (now + 4900) % 5300;
  if (hrPhaseMs < 1100) {
    const hrCycleIdx = Math.floor((now + 4900) / 5300);
    const pseudoRand = ((hrCycleIdx * 9301 + 49297) % 233280) / 233280;

    let lockX = 0, lockY = 0;
    let strikeColor = '#38bdf8';
    let isLiveDot = false;

    if (pseudoRand < 0.40) {
      // Mode A: Dynamic lock on active live moving telemetry dot
      lockX = curX;
      lockY = curY;
      strikeColor = '#fbbf24'; // Radiant amber gold
      isLiveDot = true;
    } else if (pseudoRand < 0.70) {
      // Mode B: Upper high-intensity scatter point (Z4/Z5 cluster, 320-360W)
      const highIdx = 36 + Math.floor(pseudoRand * 11);
      const offset = Math.min(SCATTER_COUNT - 1, highIdx) * SCATTER_STRIDE;
      lockX = plotX + ((scatterPool[offset] - 100) / 300) * plotW;
      lockY = plotY + plotH - ((scatterPool[offset + 1] - 100) / 90) * plotH;
      strikeColor = '#f43f5e'; // Rose sprint flare
    } else {
      // Mode C: Lower-to-mid Cartesian grid intersection
      const colIdx = Math.floor(pseudoRand * 3) % 3; // 150W, 250W, 350W
      const rowIdx = (Math.floor(pseudoRand * 7) % 3) + 1; // 120, 140, 160 BPM
      lockX = hrPwrXCoords[colIdx];
      lockY = hrGridYCoords[rowIdx];
      strikeColor = '#38bdf8'; // Electric cyan
    }

    // Phase A: Dual-Axis Sputter-On Strike (0 - 160ms) - 3 staccato lightning micro-arcs
    let strikeAlpha = 0;
    if (hrPhaseMs < 160) {
      const stutter = Math.sin(hrPhaseMs * 0.10) > 0 ? (0.50 + 0.50 * Math.sin(hrPhaseMs * 0.18)) : 0.08;
      strikeAlpha = stutter * 0.95;
    } else {
      // Phase B & C: Oscilloscope Jitter & Sputter-Off Quenching (160ms - 1100ms)
      const quenchDecay = Math.exp(-(hrPhaseMs - 160) * 0.0042);
      const dropout = (hrPhaseMs > 480 && hrPhaseMs < 600) ? 0.22 : 1.0;
      strikeAlpha = quenchDecay * dropout * 0.88;
    }

    if (strikeAlpha > 0.02) {
      ctx.save();
      // High-frequency oscilloscope raster dash jitter
      ctx.setLineDash([4, 2]);
      ctx.lineDashOffset = Math.sin(now * 0.08) * 6;

      // Dual-Axis Crosshair Hairline Flash (Vertical Column)
      ctx.strokeStyle = strikeColor === '#38bdf8' ? `rgba(56, 189, 248, ${strikeAlpha.toFixed(3)})` : (strikeColor === '#f43f5e' ? `rgba(244, 63, 94, ${strikeAlpha.toFixed(3)})` : `rgba(251, 191, 36, ${strikeAlpha.toFixed(3)})`);
      ctx.lineWidth = Math.max(1.4, 1.8 * scale);
      ctx.beginPath();
      ctx.moveTo(lockX, plotY);
      ctx.lineTo(lockX, plotY + plotH);
      ctx.stroke();

      // Dual-Axis Crosshair Hairline Flash (Horizontal Rung)
      ctx.beginPath();
      ctx.moveTo(plotX, lockY);
      ctx.lineTo(plotX + plotW, lockY);
      ctx.stroke();

      // Core White Photonic Filament
      ctx.setLineDash([]);
      ctx.strokeStyle = `rgba(255, 255, 255, ${(strikeAlpha * 0.90).toFixed(3)})`;
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.moveTo(lockX, plotY); ctx.lineTo(lockX, plotY + plotH);
      ctx.moveTo(plotX, lockY); ctx.lineTo(plotX + plotW, lockY);
      ctx.stroke();

      // Intersection Junction Radiant Lock Node
      ctx.beginPath();
      ctx.arc(lockX, lockY, Math.max(3.0, 4.5 * scale) + (isLiveDot ? Math.sin(now * 0.01) * 1.5 : 0), 0, Math.PI * 2);
      ctx.fillStyle = strikeColor;
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.2;
      ctx.stroke();

      ctx.restore();
    }
  }

  // Dynamic Linear Regression Vector Stroke
  const regX1 = plotX + ((130 - 100) / 300) * plotW;
  const regY1 = plotY + plotH - ((114 - 100) / 90) * plotH;
  const regX2 = plotX + ((380 - 100) / 300) * plotW;
  const regY2 = plotY + plotH - ((182 - 100) / 90) * plotH;

  ctx.beginPath();
  ctx.setLineDash([4, 2]);
  ctx.strokeStyle = isHovered ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.65)';
  ctx.lineWidth = Math.max(1.0, (isHovered ? 1.8 : 1.2) * scale);
  ctx.moveTo(regX1, regY1);
  ctx.lineTo(regX2, regY2);
  ctx.stroke();
  ctx.setLineDash([]);

  // Render 48 Multi-Zone Scatter Points from typed array (Zero GC)
  const dotR = Math.max(1.2, (isHovered ? 2.3 : 1.8) * scale);
  for (let i = 0; i < SCATTER_COUNT; i++) {
    const offset = i * SCATTER_STRIDE;
    const pwr = scatterPool[offset];
    const hr = scatterPool[offset + 1];
    const zone = Math.min(4, Math.max(0, Math.floor(scatterPool[offset + 4])));

    const px = plotX + ((pwr - 100) / 300) * plotW;
    const py = plotY + plotH - ((hr - 100) / 90) * plotH;

    ctx.beginPath();
    ctx.arc(px, py, dotR, 0, Math.PI * 2);
    ctx.fillStyle = ZONE_COLORS[zone];
    ctx.fill();
  }

  ctx.beginPath();
  ctx.arc(curX, curY, Math.max(2.5, (isHovered ? 5.0 : 4.0) * scale) + Math.sin(now * 0.008) * 1.5, 0, Math.PI * 2);
  ctx.strokeStyle = isHovered ? 'rgba(251, 191, 36, 0.98)' : 'rgba(251, 191, 36, 0.90)';
  ctx.lineWidth = isHovered ? 1.6 : 1.2;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(curX, curY, Math.max(1.5, (isHovered ? 2.6 : 2.2) * scale), 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();

  ctx.restore();
}

/**
 * Module 4: Telegraphic HUD Strip
 * 6 succinct telemetry indicators in a clean 3x2 digital grid (Zero GC)
 */
function drawTelegraphicHudStrip(bounds, now, alpha = panelHoverAlphas[3]) {
  const { modX, modY, modW, modH, scale = 1.0 } = computeTelemetryStripBounds(bounds);
  const isHovered = alpha >= 0.85;

  ctx.save();
  // Soft ambient drop shadow with theme-colored cyan bloom on hover
  ctx.shadowColor = isHovered ? 'rgba(56, 189, 248, 0.40)' : 'rgba(0, 0, 0, 0.60)';
  ctx.shadowBlur = Math.floor((isHovered ? 20 : 8) * scale);
  ctx.shadowOffsetY = Math.floor((isHovered ? 3 : 2) * scale);

  const cols = 3;
  const rows = 2;
  const cellGap = Math.max(2, Math.floor(4 * scale));
  const cellW = (modW - (cols - 1) * cellGap) / cols;
  const cellH = (modH - (rows - 1) * cellGap) / rows;

  const labelFontSize = Math.max(8.2, 9.5 * scale).toFixed(1);
  const valFontSize = Math.max(9.5, 12.0 * scale).toFixed(1);

  // =========================================================================
  // HUD Numeric Cell Periodic Energization & Sputter Engine
  // Period: 4100ms (Asynchronous prime-period oscillator, phase offset 3100ms)
  // Features Desynchronized Dual-Cell Ignition, Staggered Delays & Variable Quench
  // =========================================================================
  const hudPhaseMs = (now + 3100) % 4100;
  let targetCellA = -1;
  let targetCellB = -1;
  let sputterAlphaA = 0;
  let sputterAlphaB = 0;

  if (hudPhaseMs < 860) {
    const hudCycleIdx = Math.floor((now + 3100) / 4100);
    const pseudoRandA = ((hudCycleIdx * 9301 + 49297) % 233280) / 233280;
    const pseudoRandB = ((hudCycleIdx * 65537 + 19937) % 233280) / 233280;

    // Single cell (65%) vs Correlated metric pair (35%)
    if (pseudoRandA < 0.65) {
      targetCellA = Math.floor(pseudoRandA * 100) % 6;
      targetCellB = -1;
    } else {
      const pairIdx = Math.floor(((pseudoRandA - 0.65) / 0.35) * HUD_PAIRS.length) % HUD_PAIRS.length;
      targetCellA = HUD_PAIRS[pairIdx][0];
      targetCellB = HUD_PAIRS[pairIdx][1];
    }

    // Helper to evaluate single-cell sputter envelope with staggered onset & unique kinetics
    const computeCellSputter = (delayMs, igniteMs, durationMs, igniteFreq, ignitePhase, quenchRate, quenchFreq) => {
      const localT = hudPhaseMs - delayMs;
      if (localT < 0 || localT > durationMs) return 0;
      if (localT < igniteMs) {
        // Staccato micro-burst ignition
        const stutter = Math.sin(localT * igniteFreq + ignitePhase) > 0 
          ? (0.45 + 0.55 * Math.sin(localT * (igniteFreq * 2))) 
          : 0.08;
        return stutter * 0.95;
      }
      // Exponential decay with non-linear phosphor oscillation
      const decayT = (localT - igniteMs) / (durationMs - igniteMs);
      const quench = Math.exp(-quenchRate * decayT) * (1.0 + 0.35 * Math.sin(decayT * quenchFreq + ignitePhase) * (decayT < 0.6 ? 1.0 : 0.0));
      return Math.max(0, Math.min(1.0, quench));
    };

    if (targetCellA >= 0) {
      const delayA = (targetCellB >= 0 && pseudoRandA >= 0.82) ? Math.floor(40 + pseudoRandB * 100) : 0;
      const durA = Math.floor(460 + pseudoRandA * 160);
      sputterAlphaA = computeCellSputter(delayA, 130, durA, 0.085, 0.0, 3.4, 38);
    }

    if (targetCellB >= 0) {
      // Box B desynchronization: staggered onset (45-155ms delay or lead), distinct flutter frequency & variable duration
      const delayB = (pseudoRandA < 0.82) ? Math.floor(45 + pseudoRandB * 110) : 0;
      const durB = Math.floor(400 + pseudoRandB * 240);
      const igniteFreqB = 0.108 + (pseudoRandB * 0.025);
      const quenchRateB = 2.7 + (pseudoRandB * 1.1);
      sputterAlphaB = computeCellSputter(delayB, 110, durB, igniteFreqB, 1.35, quenchRateB, 48);
    }
  }

  for (let i = 0; i < 6; i++) {
    const c = i % cols;
    const r = Math.floor(i / cols);
    const cx = modX + c * (cellW + cellGap);
    const cy = modY + r * (cellH + cellGap);

    let cellAlpha = 0;
    if (i === targetCellA) cellAlpha = sputterAlphaA;
    else if (i === targetCellB) cellAlpha = sputterAlphaB;

    const isEnergized = cellAlpha > 0.01;
    const rgb = HUD_RGB[i];

    // Cell Background (illuminated cockpit surface on hover, ambient translucent at rest)
    if (isHovered) {
      const cellGrad = ctx.createLinearGradient(cx, cy, cx, cy + cellH);
      cellGrad.addColorStop(0, '#1e293b'); // slate-800 illuminated top
      cellGrad.addColorStop(1, '#0f172a'); // deep slate bottom
      ctx.fillStyle = cellGrad;
    } else {
      ctx.fillStyle = (alpha >= 0.98) ? '#0f172a' : `rgba(15, 23, 42, ${alpha.toFixed(3)})`;
    }
    ctx.fillRect(cx, cy, cellW, cellH);

    if (isEnergized) {
      // Energized wash overlay
      ctx.fillStyle = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${(0.22 * cellAlpha).toFixed(3)})`;
      ctx.fillRect(cx, cy, cellW, cellH);

      // Specular energized border stroke with luminescence bloom
      ctx.strokeStyle = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${(0.38 + 0.62 * cellAlpha).toFixed(3)})`;
      ctx.lineWidth = 1 + (cellAlpha > 0.5 ? 1 : 0);
      ctx.strokeRect(cx, cy, cellW, cellH);
    } else {
      ctx.strokeStyle = isHovered ? 'rgba(56, 189, 248, 0.85)' : 'rgba(56, 189, 248, 0.38)';
      ctx.lineWidth = isHovered ? 1.3 : 1.0;
      ctx.strokeRect(cx, cy, cellW, cellH);
    }

    // Metric Label (+25% font size, crisp high contrast)
    ctx.font = `800 ${labelFontSize}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace`;
    ctx.fillStyle = isEnergized ? '#ffffff' : (isHovered ? '#ffffff' : 'rgba(226, 232, 240, 0.98)');
    ctx.fillText(HUD_LABELS[i], cx + Math.floor(4 * scale), cy + Math.floor(11 * scale));

    // Value lookup without object allocation
    let valStr = '';
    if (i === 0) valStr = `${Math.round(telemetryState[0])} W`;
    else if (i === 1) valStr = `${Math.round(telemetryState[1])} BPM`;
    else if (i === 2) valStr = `${Math.round(telemetryState[2])} RPM`;
    else if (i === 3) valStr = `${telemetryState[3].toFixed(1)}`;
    else if (i === 4) valStr = `${telemetryState[4].toFixed(1)} MPH`;
    else if (i === 5) valStr = `${Math.round(telemetryState[5])}`;

    // Metric Value (+25% font size, vibrant high-luminance tones with specular bloom under surge)
    ctx.font = `800 ${valFontSize}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace`;
    if (isHovered) {
      ctx.shadowColor = HUD_COLORS[i];
      ctx.shadowBlur = 6;
    }
    ctx.fillStyle = (isEnergized && cellAlpha > 0.55) ? '#ffffff' : HUD_COLORS[i];
    ctx.fillText(valStr, cx + Math.floor(4 * scale), cy + Math.floor(26 * scale));
    ctx.shadowColor = 'transparent';
  }

  // Bespoke High-Tech Framing 4: Holographic Specular Shimmer & Precision Corner Brackets
  // Measured 5800ms period traveling glint along perimeter (replaces cheap moving dotted line)
  const hudGlintT = ((now + 3400) * 0.00028) % 1.0;
  const hudGlintSpan = Math.max(40, Math.floor(65 * scale));
  const hudGlintX = (modX - 2) + hudGlintT * ((modW + 4) + hudGlintSpan) - hudGlintSpan * 0.5;
  const hudGlintGrad = ctx.createLinearGradient(hudGlintX - hudGlintSpan * 0.5, modY - 2, hudGlintX + hudGlintSpan * 0.5, modY - 2);
  hudGlintGrad.addColorStop(0, 'rgba(56, 189, 248, 0)');
  hudGlintGrad.addColorStop(0.3, 'rgba(56, 189, 248, 0.45)');
  hudGlintGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.95)');
  hudGlintGrad.addColorStop(0.7, 'rgba(56, 189, 248, 0.55)');
  hudGlintGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');

  const matrixPulse = (now * 0.0014) % (Math.PI * 2);
  const matrixAlpha = 0.48 + Math.sin(matrixPulse) * 0.28;
  ctx.strokeStyle = isHovered ? hudGlintGrad : `rgba(56, 189, 248, ${(matrixAlpha * 0.65).toFixed(3)})`;
  ctx.lineWidth = isHovered ? 1.6 : 1.2;
  ctx.strokeRect(modX - 2, modY - 2, modW + 4, modH + 4);

  if (isHovered) {
    ctx.strokeStyle = hudGlintGrad;
    ctx.lineWidth = 1.8;
    ctx.strokeRect(modX - 2, modY - 2, modW + 4, modH + 4);
  }

  const bLen = Math.max(4, Math.floor(7 * scale));
  ctx.strokeStyle = isHovered ? 'rgba(56, 189, 248, 0.98)' : 'rgba(56, 189, 248, 0.75)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  // Top-left bracket
  ctx.moveTo(modX - 2, modY - 2 + bLen); ctx.lineTo(modX - 2, modY - 2); ctx.lineTo(modX - 2 + bLen, modY - 2);
  // Top-right bracket
  ctx.moveTo(modX + modW + 2 - bLen, modY - 2); ctx.lineTo(modX + modW + 2, modY - 2); ctx.lineTo(modX + modW + 2, modY - 2 + bLen);
  // Bottom-left bracket
  ctx.moveTo(modX - 2, modY + modH + 2 - bLen); ctx.lineTo(modX - 2, modY + modH + 2); ctx.lineTo(modX - 2 + bLen, modY + modH + 2);
  // Bottom-right bracket
  ctx.moveTo(modX + modW + 2 - bLen, modY + modH + 2); ctx.lineTo(modX + modW + 2, modY + modH + 2); ctx.lineTo(modX + modW + 2, modY + modH + 2 - bLen);
  ctx.stroke();

  ctx.restore();
}

/**
 * Main Right-Margin Performance Telemetry Dashboard Master Routine
 */
export function drawPerformanceDashboard(now, deltaMs) {
  if (!ctx) return;
  const bounds = computeDashboardBounds(width, height);
  if (!bounds) return;

  // 1. Update live telemetry state (Zero GC)
  updateTelemetry(now);

  // 2. Evaluate interactive panel hover state & lerp alphas (Zero GC)
  activeHoveredPanelIdx = getHoveredPanelIndex(bounds, mouseX, mouseY);
  for (let i = 0; i < 4; i++) {
    const targetAlpha = (i === activeHoveredPanelIdx) ? 1.0 : 0.72;
    panelHoverAlphas[i] += (targetAlpha - panelHoverAlphas[i]) * 0.18;
  }

  // 3. Draw 4 detached standalone floating island HUD widgets (with dynamic hover opacity)
  drawPowerDurationModule(bounds, now, panelHoverAlphas[0]);
  drawVo2MaxModule(bounds, now, panelHoverAlphas[1]);
  drawHrPwrScatterModule(bounds, now, panelHoverAlphas[2]);
  drawTelegraphicHudStrip(bounds, now, panelHoverAlphas[3]);
}

/**
 * Exported Standalone Decoupled HUD Component Drawing Functions
 */
export function drawPdcComponent(targetCtx, bounds, now, alpha = panelHoverAlphas[0]) {
  const prevCtx = ctx;
  if (targetCtx) ctx = targetCtx;
  drawPowerDurationModule(bounds, now, alpha);
  ctx = prevCtx;
}

export function drawVo2MaxComponent(targetCtx, bounds, now, alpha = panelHoverAlphas[1]) {
  const prevCtx = ctx;
  if (targetCtx) ctx = targetCtx;
  drawVo2MaxModule(bounds, now, alpha);
  ctx = prevCtx;
}

export function drawHrPwrComponent(targetCtx, bounds, now, alpha = panelHoverAlphas[2]) {
  const prevCtx = ctx;
  if (targetCtx) ctx = targetCtx;
  drawHrPwrScatterModule(bounds, now, alpha);
  ctx = prevCtx;
}

export function drawTelemetryStripComponent(targetCtx, bounds, now, alpha = panelHoverAlphas[3]) {
  const prevCtx = ctx;
  if (targetCtx) ctx = targetCtx;
  drawTelegraphicHudStrip(bounds, now, alpha);
  ctx = prevCtx;
}

/**
 * Main Render Frame Execution
 */
function render(now) {
  if (!ctx) return;

  // Fully halt (no vsync spinning) while a modal is open or the page is in ambient idle.
  // A body-class MutationObserver (setupLifecycleListeners) restarts the loop on resume.
  if (isRenderSuspended()) {
    rafId = null;
    return;
  }

  const deltaMs = Math.min(now - (lastTime || now), 33.33); // Clamp delta to 30 FPS boundary to prevent jumps
  lastTime = now;

  // Clear main canvas with slight transparency or full clear
  ctx.clearRect(0, 0, width, height);

  const bounds = computeCoverBounds(width, height, 1024, 559);

  // 1. Draw Offscreen Topographic Elevation Contours (slow drifting phase)
  if (offscreenCanvas) {
    topoPhase = (topoPhase + deltaMs * 0.008) % (width || 800);
    // Draw twice to create seamless wrap
    ctx.drawImage(offscreenCanvas, -topoPhase, 0);
    ctx.drawImage(offscreenCanvas, -topoPhase + (offscreenCanvas.width / 2), 0);
  }

  // 2. Draw Volumetric Crepuscular God Rays
  drawCrepuscularRays(now);

  // 3. Draw Lockstep Frame Laser Circuit (traces bicycle diamond frame & carbon tubes)
  drawFrameLaserCircuit(now, bounds);

  // 3.1 Draw Specialized Aethos Laser-Tracing & Energized Bloom Animation
  drawAethosLaserTrace(now, bounds);

  // 4. Draw Luminescent Bearing Nodes
  drawHubGlowNodes(now, bounds);

  // 5. Draw Lockstep Exploded Hub Axle Energy Pulse
  drawExplodedAxlePulse(now, bounds);

  // 6. Draw Drivetrain Matrix Luminescent Shimmer (Upper-right clean of gears)
  drawMatrixShimmer(now, bounds);

  // 7. Draw CAD Laser Scan Sweep
  drawLaserScan(now);

  // 8. Draw Concentric Bearing Ripple Rings
  drawBearingRipples(now);

  // NOTE: Procedural spinning gears (drawChainring) in upper-right are PERMANENTLY REMOVED
  // ensuring the authentic Drivetrain Optimization schematic is 100% visible and unoccluded.

  // 9. Draw Aerodynamic Streamlines
  drawStreamlines(now);

  // 10. Update & Render Zero-GC Particle Motons
  updateAndRenderParticles(deltaMs);

  // 11. Draw Interactive UV Blacklight Flashlight & HUD Reticle
  // Project real-time cursor position into canvas coordinates to eliminate scale/drift offset
  if (canvas && clientMouseX >= 0 && clientMouseY >= 0) {
    const rect = canvas.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      mouseX = (clientMouseX - rect.left) * (width / rect.width);
      mouseY = (clientMouseY - rect.top) * (height / rect.height);
    }
  }

  drawInteractiveUvFlashlight();
  drawHudCrosshair();

  // 12. Draw Right-Margin Cycling Performance Telemetry Dashboard
  drawPerformanceDashboard(now, deltaMs);

  // Loop request
  rafId = requestAnimationFrame(render);
}

/**
 * Draw a single static blueprint frame for prefers-reduced-motion
 */
function renderStaticFrame() {
  if (!ctx) return;
  ctx.clearRect(0, 0, width, height);
  if (offscreenCanvas) {
    ctx.drawImage(offscreenCanvas, 0, 0);
  }
  const bounds = computeCoverBounds(width, height, 1024, 559);
  drawCrepuscularRays(1500);
  drawFrameLaserCircuit(1500, bounds);
  drawAethosLaserTrace(4500, bounds); // Static frame renders energized glow state
  drawHubGlowNodes(1500, bounds);
  drawExplodedAxlePulse(1500, bounds);
  drawMatrixShimmer(1500, bounds);
  drawLaserScan(1500);
  drawBearingRipples(1500);
  drawStreamlines(1000);
  drawPerformanceDashboard(1500, 16);
}

/**
 * Handle window resize & high-DPI canvas buffer
 */
function handleResize() {
  if (!canvas) return;
  dpr = window.devicePixelRatio || 1;
  const clientW = (typeof document !== 'undefined' && document.documentElement && document.documentElement.clientWidth) || (typeof window !== 'undefined' && window.innerWidth) || 1024;
  const clientH = (typeof document !== 'undefined' && document.documentElement && document.documentElement.clientHeight) || (typeof window !== 'undefined' && window.innerHeight) || 559;
  width = clientW + 50; // Inset: -25px left & right
  height = clientH + 50; // Inset: -25px top & bottom

  canvas.width = Math.floor(width * dpr);
  canvas.height = Math.floor(height * dpr);
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  if (ctx) {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  renderOffscreenTopo();

  // Re-seed particles smoothly across viewport
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    initParticle(i, true);
  }
}

function isRenderSuspended() {
  if (typeof document === 'undefined' || !document.body) return false;
  const cl = document.body.classList;
  return cl.contains('modal-open') || cl.contains('is-ambient-idle');
}

/**
 * Restart the render loop if it is halted and no suspension condition applies.
 */
export function resumeAmbientWallpaper() {
  if (!ctx || rafId || document.hidden || isRenderSuspended()) return;
  const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) return;
  lastTime = performance.now();
  rafId = requestAnimationFrame(render);
}

/**
 * Setup lifecycle listeners (visibility sleep & reduced-motion)
 */
function setupLifecycleListeners() {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    } else {
      resumeAmbientWallpaper();
    }
  });

  // Resume when modal-open / is-ambient-idle is removed from <body>
  if (typeof MutationObserver !== 'undefined' && document.body) {
    new MutationObserver(() => {
      if (isRenderSuspended()) {
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      } else {
        resumeAmbientWallpaper();
      }
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  window.addEventListener('resize', () => {
    handleResize();
  }, { passive: true });

  window.addEventListener('pointermove', (e) => {
    clientMouseX = e.clientX;
    clientMouseY = e.clientY;
    mouseTargetAlpha = 0.65;

    if (window.innerWidth > 0 && window.innerHeight > 0) {
      const px = ((e.clientX / window.innerWidth) - 0.5) * -16;
      const py = ((e.clientY / window.innerHeight) - 0.5) * -14;
      document.documentElement.style.setProperty('--parallax-x', `${px.toFixed(1)}px`);
      document.documentElement.style.setProperty('--parallax-y', `${py.toFixed(1)}px`);
    }
  }, { passive: true });

  window.addEventListener('pointerleave', () => {
    mouseTargetAlpha = 0;
    clientMouseX = -100;
    clientMouseY = -100;
    mouseX = -100;
    mouseY = -100;
    document.documentElement.style.setProperty('--parallax-x', '0px');
    document.documentElement.style.setProperty('--parallax-y', '0px');
  }, { passive: true });
}

/**
 * Initialize the Ambient Wallpaper Canvas
 */
export function initAmbientWallpaper(canvasId = 'ambient-canvas') {
  canvas = document.getElementById(canvasId);
  if (!canvas) return false;

  ctx = canvas.getContext('2d', { alpha: true });
  if (!ctx) return false;

  handleResize();
  setupLifecycleListeners();

  const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) {
    renderStaticFrame();
    return true;
  }

  lastTime = performance.now();
  rafId = requestAnimationFrame(render);
  return true;
}

/**
 * Test & Debug Inspection Exports
 */
export function getParticleCount() {
  return PARTICLE_COUNT;
}

export function isAnimationActive() {
  return rafId !== null;
}

export function stopAmbientWallpaper() {
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
}
